import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import React__default, { useContext, useRef, useEffect, useState, forwardRef, Component, useMemo, useLayoutEffect } from 'react';
import classnames from 'classnames';
import { Icon } from '@grandvisionhq/icons';
import { useEventListener, createImageUrl, placeholderImage, useInterval, useDynamicModule, useDebouncedEffect, useScrollIntoView, QueryString, StringGuard, formatPrice, isNumber, sortPrescriptionParameters, PrescriptionValueUtils, isPresent, isNil as isNil$1, parseHtml, domToReact } from '@grandvisionhq/utils';
import uniq$1 from 'lodash/uniq';
import { motion, AnimatePresence } from 'framer-motion';
import { Heading, Button as Button$1, Text, Checkbox, ResponsiveImage, Hr, variantMap, SkeletonLoader } from '@grandvisionhq/elements';
import useOnClickOutside from 'use-onclickoutside';
import { useIntl, withIntl } from '@grandvisionhq/state';
import SwipeableViews from 'react-swipeable-views';
import * as ReactDOM from 'react-dom';
import ReactDOM__default from 'react-dom';
import get from 'lodash/get';
import { useInView } from 'react-intersection-observer';
import FocusTrap from 'focus-trap-react';
import { useCookieConsentContext, isCookieCategoryEnabled } from '@grandvisionhq/cookie-consent';
import isNil from 'lodash/isNil';
import { Range, Handle } from 'rc-slider';
import StickyNode from 'react-stickynode';
import { Manager, Reference, Popper } from 'react-popper';
import { toast, ToastContainer as ToastContainer$2, Slide } from 'react-toastify';
import { BreadcrumbList } from '@grandvisionhq/analytics';

const Link = /*#__PURE__*/ React.forwardRef((_param, ref)=>{
    var { url , href , target , className , label ='' , download , children , hidden , ariaHasPopup , ariaIsExpanded , onClick , onKeyDown , onFocus , openInNewWindow , iconBefore , iconAfter  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "url",
        "href",
        "target",
        "className",
        "label",
        "download",
        "children",
        "hidden",
        "ariaHasPopup",
        "ariaIsExpanded",
        "onClick",
        "onKeyDown",
        "onFocus",
        "openInNewWindow",
        "iconBefore",
        "iconAfter"
    ]);
    const isNewWindow = target === 'external' || target === '_blank' || openInNewWindow;
    const targetOpt = isNewWindow ? '_blank' : undefined;
    const rel = isNewWindow ? 'noreferrer noopener' : undefined;
    const classes = classnames(className, {
        'link--with-icon': iconBefore || iconAfter
    }) || undefined;
    return(/* The linter throws an error that <a> does not support aria-haspopup.
       * However, the docs disagree and it has been shown to work in our testing with VoiceOver on Chrome
       * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-haspopup#associated_roles
       * */ // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    /*#__PURE__*/ React.createElement("a", {
        ref: ref,
        className: classes,
        href: href || url || '#',
        target: targetOpt,
        rel: rel,
        "aria-hidden": hidden,
        download: download,
        "aria-haspopup": ariaHasPopup,
        "aria-expanded": ariaHasPopup ? ariaIsExpanded : undefined,
        onClick: onClick,
        onKeyDown: onKeyDown,
        onFocus: onFocus,
        "data-t": rest['data-t']
    }, renderLinkIcon(iconBefore), children ? children : label, renderLinkIcon(iconAfter)));
});
Link.displayName = 'Link';
function renderLinkIcon(icon) {
    return icon ? /*#__PURE__*/ React.createElement(Icon, {
        name: icon
    }) : null;
}

var Link$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': Link
});

var BreakpointName;
(function(BreakpointName) {
    BreakpointName["PHONE"] = "PHONE";
    BreakpointName["TABLET_PORTRAIT"] = "TABLET_PORTRAIT";
    BreakpointName["TABLET_LANDSCAPE"] = "TABLET_LANDSCAPE";
    BreakpointName["DESKTOP"] = "DESKTOP";
})(BreakpointName || (BreakpointName = {}));
const MediaQueryContext = /*#__PURE__*/ React__default.createContext({});
const useBreakpoint = ()=>{
    const { currentBreakpoint  } = useContext(MediaQueryContext);
    return currentBreakpoint;
};
const useIsBreakpoint = (name)=>{
    const { currentBreakpoint  } = useContext(MediaQueryContext);
    return currentBreakpoint === name;
};
const useIsPhone = ()=>useIsBreakpoint(BreakpointName.PHONE)
;
const useIsTabletPortrait = ()=>useIsBreakpoint(BreakpointName.TABLET_PORTRAIT)
;
const useIsTabletLandscape = ()=>useIsBreakpoint(BreakpointName.TABLET_LANDSCAPE)
;
const useIsDesktop = ()=>useIsBreakpoint(BreakpointName.DESKTOP)
;
const breakpointRange = [
    BreakpointName.PHONE,
    BreakpointName.TABLET_PORTRAIT,
    BreakpointName.TABLET_LANDSCAPE,
    BreakpointName.DESKTOP, 
];
// Check if the current breakpoint is above the argument
const useIsAbove = (name)=>{
    const { currentBreakpoint  } = useContext(MediaQueryContext);
    return breakpointRange.indexOf(currentBreakpoint) > breakpointRange.indexOf(name);
};
// Check if the current breakpoint is below the argument
const useIsBelow = (name)=>{
    const { currentBreakpoint  } = useContext(MediaQueryContext);
    return breakpointRange.indexOf(currentBreakpoint) < breakpointRange.indexOf(name);
};
function WithBreakpoints(Component) {
    const wrappedName = Component.displayName || Component.name || 'Component';
    const withBreakpoints = (props)=>{
        const currentBreakpoint = useBreakpoint();
        return /*#__PURE__*/ React__default.createElement(Component, swcHelpers.extends({}, props, {
            currentBreakpoint: currentBreakpoint
        }));
    };
    withBreakpoints.displayName = `WithBreakpoints(${wrappedName})`;
    return withBreakpoints;
}

const Breakpoints = {
    M: 768,
    L: 1024,
    XL: 1440
};
/**
 * Checks current viewport width against our Breakpoints enumeration
 * and returns the one that is currently active
 */ const isMinWidth = (width)=>window.matchMedia(`(min-width: ${width}px)`).matches
;
const getCurrentBreakpoint = ()=>{
    if (isMinWidth(Breakpoints.XL)) {
        return BreakpointName.DESKTOP;
    } else if (isMinWidth(Breakpoints.L)) {
        return BreakpointName.TABLET_LANDSCAPE;
    } else if (isMinWidth(Breakpoints.M)) {
        return BreakpointName.TABLET_PORTRAIT;
    } else {
        return BreakpointName.PHONE;
    }
};

const MediaQueryProvider = ({ children , initialBreakpoint =BreakpointName.PHONE ,  })=>{
    const [currentBreakpoint, setBreakpoint] = React.useState(initialBreakpoint);
    // Initial client mount update desktop to mobile if screensize is small.
    React.useEffect(()=>{
        setBreakpoint(getCurrentBreakpoint());
    }, []);
    const handleResize = React.useCallback(()=>{
        setBreakpoint(getCurrentBreakpoint());
    }, []);
    useEventListener('resize', handleResize);
    return /*#__PURE__*/ React.createElement(MediaQueryContext.Provider, {
        value: {
            currentBreakpoint
        }
    }, children);
};

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Context: MediaQueryContext,
  get BreakpointName () { return BreakpointName; },
  WithBreakpoints: WithBreakpoints,
  useBreakpoint: useBreakpoint,
  useIsAbove: useIsAbove,
  useIsBelow: useIsBelow,
  useIsDesktop: useIsDesktop,
  useIsPhone: useIsPhone,
  useIsTabletLandscape: useIsTabletLandscape,
  useIsTabletPortrait: useIsTabletPortrait,
  Provider: MediaQueryProvider,
  Breakpoints: Breakpoints,
  getCurrentBreakpoint: getCurrentBreakpoint
});

var sharedProps = /*#__PURE__*/Object.freeze({
  __proto__: null
});

var PrescriptionType;
(function(PrescriptionType) {
    PrescriptionType["EXISTING_CUSTOMER"] = 'ExistingCustomer';
    PrescriptionType["NEW_PRESCRIPTION"] = 'NewPrescription';
    PrescriptionType["PRESCRIPTION_FILE"] = 'PrescriptionFile';
    PrescriptionType["EMAIL_PRESCRIPTION"] = 'EmailPrescription';
})(PrescriptionType || (PrescriptionType = {}));

var types = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get PrescriptionType () { return PrescriptionType; }
});

function convertBrand(brand) {
    const query = /&/g;
    return brand.replace(query, '%26').toLowerCase();
}

/**
 * Converts a style string from the CMS to separate props to set on a Button component.
 * E.G. "secondary outline" => { theme: secondary, outline: true }
 *
 * This would be better suited to live in the bloomreach package,
 * but we can't add that dependency to common.
 */ function createButtonStyles(linkStyle) {
    const defaultButtonStyles = {
        color: 'primary',
        variant: 'solid'
    };
    const styles = linkStyle && linkStyle.split(' ');
    if (!styles || !styles.length) {
        return defaultButtonStyles;
    }
    return {
        color: styles.includes('secondary') ? 'secondary' : 'primary',
        variant: styles.includes('outline') ? 'outline' : 'solid'
    };
}

function setCustomEventPolyfill() {
    function CustomEvent(event, params = {
        bubbles: false,
        cancelable: false,
        detail: undefined
    }) {
        const evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
        return evt;
    }
    CustomEvent.prototype = Event.prototype;
    window.CustomEvent = CustomEvent;
}
const createCustomEvent = (eventName)=>{
    if (typeof window.CustomEvent !== 'function') {
        setCustomEventPolyfill();
    }
    return new window.CustomEvent(eventName);
};

const getKeyByValue = (object, value)=>Object.keys(object).find((key)=>object[key] === value
    ) || null
;

const getQueryParamByKey = (key)=>{
    const query = window.location.search.substring(1);
    const params = query.split('&');
    for(let i = 0; i < params.length; i++){
        const pair = params[i].split('=');
        if (decodeURIComponent(pair[0]) === key) {
            return decodeURIComponent(pair[1]);
        }
    }
};

function throttle(fn, delay) {
    let canCall = true;
    return (...args)=>{
        if (canCall) {
            // eslint-disable-next-line prefer-spread
            fn.apply(null, args);
            canCall = false;
            setTimeout(()=>{
                canCall = true;
            }, delay);
        }
    };
}

const pad = function(num) {
    const norm = Math.floor(Math.abs(num));
    return (norm < 10 ? '0' : '') + norm;
};
/**
 * Formats a date according to ISO format
 * and adds timezone at the end
 * @param {Date} date
 * @returns {string}
 */ const toISOString = (date)=>{
    const tzo = -date.getTimezoneOffset();
    const dif = tzo >= 0 ? '+' : '-';
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds()) + '.' + pad(date.getMilliseconds()) + dif + pad(tzo / 60) + ':' + pad(tzo % 60);
};

const truncateString = (text, desiredLength)=>text.length > desiredLength ? text.substring(0, desiredLength - 1) + '…' : text
;

const easeInOutQuad = (currentTime, start, change, duration)=>{
    currentTime /= duration / 2;
    if (currentTime < 1) {
        return change / 2 * currentTime * currentTime + start;
    }
    currentTime--;
    return -change / 2 * (currentTime * (currentTime - 2) - 1) + start;
};
const scrollToItem = (elem, to, duration)=>{
    const start = elem.scrollTop;
    const change = to - start;
    const startDate = +new Date();
    const animateScroll = function() {
        const currentDate = +new Date();
        const currentTime = currentDate - startDate;
        elem.scrollTop = parseInt(easeInOutQuad(currentTime, start, change, duration));
        if (window && currentTime < duration) {
            window.requestAnimationFrame(animateScroll);
        } else {
            elem.scrollTop = to;
        }
    };
    animateScroll();
};

const SCROLL_LOCKED_CLASS = 'scroll-locked';
const getLockElement = ()=>typeof document === 'undefined' ? null : document.body || null
;
const isScrollLocked = (lockElement)=>{
    const element = lockElement || getLockElement();
    return element ? element.classList.contains(SCROLL_LOCKED_CLASS) : false;
};
const getScrollY = (scrollElement)=>{
    if (!scrollElement && typeof window === 'undefined') {
        return 0;
    }
    return scrollElement ? scrollElement.scrollTop : window.pageYOffset;
};
const setScrollY = (position, scrollElement)=>{
    if (!scrollElement && typeof window === 'undefined') {
        return;
    }
    if (scrollElement) {
        scrollElement.scrollTop = position;
    } else {
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollTo#parameters
        // 'instant' is a valid parameter and it's working but TS was complaining -\_/-
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.scrollTo({
            left: window.pageXOffset,
            top: position,
            behavior: 'instant'
        });
    }
};
const lockScroll = (position, scrollElement, lockElement)=>{
    const scrollY = position || getScrollY(scrollElement);
    const element = lockElement || getLockElement();
    if (!element) {
        return;
    }
    element.classList.add(SCROLL_LOCKED_CLASS);
    element.style.overflowY = 'hidden';
    element.style.position = 'fixed';
    element.style.top = `-${scrollY}px`;
};
const unlockScroll = (position, scrollElement, lockElement)=>{
    const element = lockElement || getLockElement();
    if (element === null) {
        return;
    }
    const top = position ? undefined : element.style.top;
    const scrollY = top ? Math.abs(parseInt(top, 10)) : position || 0;
    let requestID;
    let frameTickCount = 0;
    const resetStyling = ()=>{
        element.classList.remove(SCROLL_LOCKED_CLASS);
        element.style.overflowY = '';
        element.style.position = '';
        resetTopStyling();
    };
    const resetTopStyling = ()=>{
        const removeTopStyling = ()=>{
            if (// Check if all the related initial `scroll-lock styling' has been removed
            !element.classList.contains(SCROLL_LOCKED_CLASS) && !element.style.overflowY && !element.style.position) {
                // Only remove the top styling when the `frameTickCount is 2 or greater`, because if it is lower than 2, the body element scrolls back to the top of the page
                if (frameTickCount >= 2) {
                    element.style.top = '';
                    cancelAnimationFrame(requestID);
                } else {
                    // Only start to increase the frameTickCount after the related  initial `scroll-lock styling' has been removed
                    ++frameTickCount;
                    requestID = requestAnimationFrame(removeTopStyling);
                }
            }
        };
        // Added a `requestAnimationFrame` for removing the `top` css property,
        // which prevents the body element from unnecessary scrolling to the top of the page
        requestID = requestAnimationFrame(removeTopStyling);
    };
    resetStyling();
    setScrollY(scrollY, scrollElement);
};

var windowOrGlobal = typeof self === 'object' && self.self === self && self || typeof global === 'object' && global.global === global && global || window;

// https://github.com/Modernizr/Modernizr/blob/master/feature-detects/touchevents.js
const mq = (query)=>window.matchMedia(query).matches
;
// Only works on client side
const isTouchDevice = ()=>{
    if (typeof window === 'undefined') {
        return false;
    }
    const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
    // @ts-ignore DocumentTouch is not recognized
    if ('ontouchstart' in window || window.DocumentTouch && document instanceof DocumentTouch) {
        return true;
    }
    const query = [
        '(',
        prefixes.join('touch-enabled),('),
        'heartz',
        ')'
    ].join('');
    return mq(query);
};

const colorHexMap = {
    black: '#000000',
    white: '#ffffff',
    grey: '#f5f5f5'
};

const usePrevious = (value)=>{
    const ref = useRef();
    useEffect(()=>{
        ref.current = value;
    });
    return ref.current;
};

const useHeaderOnScroll = (isMobile)=>{
    const [isHidden, setIsHidden] = React.useState(false);
    const [isCondensedHeader, setIsCondensedHeader] = React.useState(false);
    const headerRef = React.useRef(null);
    const lastScrollTop = React.useRef(0);
    const headerHeight = React.useRef(0);
    /**
   * Scroll handler toggling header visibility on mobile
   */ const handleScroll = React.useCallback(()=>{
        const threshold = 10;
        const scrollTop = window.pageYOffset;
        // Check if we passed the threshold, or if the scroll is currently locked before we do anything
        if (Math.abs(lastScrollTop.current - scrollTop) <= threshold || isScrollLocked()) {
            return;
        }
        setIsHidden(scrollTop > lastScrollTop.current && scrollTop > headerHeight.current);
        lastScrollTop.current = scrollTop;
    }, [
        setIsHidden
    ]);
    /** Adds scroll listener to header */ React.useEffect(()=>{
        if (isMobile) {
            if (headerRef.current) {
                headerHeight.current = headerRef.current.offsetHeight;
            }
            window.addEventListener('scroll', handleScroll);
        }
        return ()=>window.removeEventListener('scroll', handleScroll)
        ;
    }, [
        isMobile,
        handleScroll
    ]);
    React.useEffect(()=>{
        if (!isMobile) {
            const handleDesktopScroll = ()=>setIsCondensedHeader(window.pageYOffset > 50)
            ;
            handleDesktopScroll();
            window.addEventListener('scroll', handleDesktopScroll);
            return ()=>window.removeEventListener('scroll', handleDesktopScroll)
            ;
        }
    }, [
        isMobile
    ]);
    return React.useMemo(()=>({
            isHidden,
            isCondensedHeader,
            headerRef
        })
    , [
        isHidden,
        isCondensedHeader,
        headerRef
    ]);
};

const getCartTotalsTitle = (numberOfItems, intl)=>{
    const singularLabelKey = 'cart.minicart.totalsTitle.singular';
    const pluralLabelKey = 'cart.minicart.totalsTitle';
    const labelKey = numberOfItems === 1 && intl.hasLabel(singularLabelKey) ? singularLabelKey : pluralLabelKey;
    return intl.getFormattedLabel(labelKey, {
        labelData: {
            numberOfItems
        }
    });
};

const replaceQueryParam = (paramSet, search)=>{
    const { name , newValue  } = paramSet;
    const regex = new RegExp(`([?;&])${name}[^&;]*[;&]?`);
    const query = search.replace(regex, '$1').replace(/&$/, '');
    const queryPrefix = query.length > 2 ? `${query}&` : '?';
    return `${queryPrefix}${name}=${newValue}`;
};

/**
 * @description Replace multiple query parameters without `URLSearchParams`
 * This is a way to replace query parameters in a string
 * without unwanted encoding other properties
 */ const replaceQueryParams = (paramSets, search)=>{
    return paramSets.reduce((prev, { name , newValue  })=>{
        return replaceQueryParam({
            name,
            newValue
        }, prev);
    }, search);
};

const MAX_IMAGE_WIDTH = 2880;
const createImgUrl = (url, params = {})=>{
    return `${createImageUrl(url, swcHelpers.objectSpread({}, params))}`;
};
const clampWidth = (width)=>{
    // We won't render images wider than MAX_IMAGE_WIDTH
    return width > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH : width;
};
const createImageSrcSetItem = (url, width, imageParams)=>{
    const clampedWidth = clampWidth(width);
    return `${createImageUrl(url, swcHelpers.objectSpread({}, imageParams, {
        width: clampedWidth
    }, clampedWidth > 1920 ? {
        quality: 50
    } : {}))} ${clampedWidth}w`;
};
const createImageSrcSet = (url, imageSizes, imageParams, opts)=>{
    // Get all pixel densities that we're going to take into account
    const allPds = (opts === null || opts === void 0 ? void 0 : opts.extraPixelDensities) && opts.extraPixelDensities.length > 0 ? [
        ...opts.extraPixelDensities,
        1
    ] : [
        1
    ];
    const originalImageSizeValues = Object.values(imageSizes).filter(Boolean);
    const imageSizeValues = uniq$1(allPds.map((pd)=>originalImageSizeValues.map((val)=>clampWidth(val * pd)
        )
    ).flat()).sort((a, b)=>a - b
    );
    return imageSizeValues.map((size)=>createImageSrcSetItem(url, size, imageParams)
    ).join(', ');
};
const getSize = (pixelDensity, maxWidth, imageSize)=>{
    const highPdCondition = pixelDensity > 1 ? `(min-resolution: ${pixelDensity}dppx)` : '';
    const maxWidthCondition = maxWidth ? `(max-width: ${maxWidth}px)` : '';
    const union = highPdCondition && maxWidthCondition ? ' and ' : '';
    return `${highPdCondition}${union}${maxWidthCondition} ${clampWidth(pixelDensity * imageSize)}px`.trim();
};
const createImageSizes = (imageSizes, opts)=>{
    // Get all pixel densities that we're going to take into account
    const allPds = (opts === null || opts === void 0 ? void 0 : opts.extraPixelDensities) && opts.extraPixelDensities.length > 0 ? [
        ...opts.extraPixelDensities.sort().reverse(),
        1
    ] : [
        1
    ];
    let result = Object.entries(Breakpoints)// important to keep the order because the browser will pick the first image that matches the condition
    .sort(([, a], [, b])=>a - b
    ).map(([bpKey, bpValue])=>{
        const imageSize = imageSizes[bpKey];
        if (!imageSize) {
            return undefined;
        }
        return allPds.map((pd)=>getSize(pd, bpValue, imageSize)
        ).join(', ');
    }).filter(Boolean).join(', ');
    // for viewports above Breakpoints.XL
    const maxImageSize = imageSizes.MAX;
    if (maxImageSize) {
        const desktopSize = allPds.map((pd)=>getSize(pd, undefined, maxImageSize)
        ).join(', ');
        result = `${result}${result ? ', ' : ''}${desktopSize}`;
    }
    return result;
};

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  convertBrand: convertBrand,
  createButtonStyles: createButtonStyles,
  createCustomEvent: createCustomEvent,
  getKeyByValue: getKeyByValue,
  getQueryParamByKey: getQueryParamByKey,
  throttle: throttle,
  toISOString: toISOString,
  truncateString: truncateString,
  scrollToItem: scrollToItem,
  windowOrGlobal: windowOrGlobal,
  isTouchDevice: isTouchDevice,
  colorHexMap: colorHexMap,
  usePrevious: usePrevious,
  useHeaderOnScroll: useHeaderOnScroll,
  getCartTotalsTitle: getCartTotalsTitle,
  replaceQueryParam: replaceQueryParam,
  replaceQueryParams: replaceQueryParams,
  SCROLL_LOCKED_CLASS: SCROLL_LOCKED_CLASS,
  lockScroll: lockScroll,
  isScrollLocked: isScrollLocked,
  unlockScroll: unlockScroll,
  getScrollY: getScrollY,
  setScrollY: setScrollY,
  createImgUrl: createImgUrl,
  createImageSrcSetItem: createImageSrcSetItem,
  createImageSrcSet: createImageSrcSet,
  createImageSizes: createImageSizes
});

var Accordion = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React.createElement("article", {
        className: classnames('accordion', className),
        "data-t": rest['data-t'] || 'accordion'
    }, children);
};

const animations$1 = {
    expanded: {
        height: 'auto',
        opacity: 1,
        transition: {
            ease: 'easeOut',
            duration: 0.15
        },
        overflow: 'visible',
        display: 'block'
    },
    collapsed: {
        height: '0px',
        opacity: 0,
        transition: {
            ease: 'easeIn',
            duration: 0.15
        },
        overflow: 'hidden',
        transitionEnd: {
            display: 'none'
        }
    }
};
const AccordionItem = ({ id , label , className , children , onClick , headingLevel ='h2' , expanded: expanded1 = false , variant ='normal' , fullWidth ,  })=>{
    const [isExpanded, setIsExpanded] = React.useState(expanded1);
    const [hasFocus, setHasFocus] = React.useState(false);
    const handleClick = React.useCallback(()=>{
        setIsExpanded((expanded)=>{
            onClick && onClick(!expanded);
            return !expanded;
        });
    }, []);
    const handleFocus = React.useCallback(()=>setHasFocus(true)
    , []);
    const handleBlur = React.useCallback(()=>setHasFocus(false)
    , []);
    // Allow prop updates to override the local state
    React.useEffect(()=>{
        if (expanded1 !== isExpanded) {
            setIsExpanded(expanded1);
        }
    }, [
        expanded1
    ]);
    return /*#__PURE__*/ React.createElement("section", {
        "data-t": "accordion-item",
        className: classnames(className, 'accordion-item', {
            [`accordion-item--${variant}`]: variant
        })
    }, /*#__PURE__*/ React.createElement("button", {
        id: `${id}-control`,
        className: classnames('accordion-item__head', {
            [`${className}__head`]: className,
            'accordion-item__head--full-width': fullWidth
        }),
        "aria-label": label,
        "aria-controls": id,
        "aria-expanded": isExpanded,
        onClick: handleClick,
        onFocus: handleFocus,
        onBlur: handleBlur
    }, /*#__PURE__*/ React.createElement(Heading, {
        component: headingLevel,
        className: classnames('accordion-item__label', {
            [`${className}__label`]: className
        }),
        variant: "smallest"
    }, label), /*#__PURE__*/ React.createElement(Icon, {
        name: isExpanded ? 'chevron-up' : 'chevron-down',
        className: classnames('accordion-item__icon', {
            [`${className}__icon`]: className
        }),
        height: 32,
        width: 32
    })), /*#__PURE__*/ React.createElement(motion.div, {
        id: id,
        className: classnames('accordion-item__body', {
            [`${className}__body`]: className,
            'accordion-item__body--full-width': fullWidth
        }),
        "aria-labelledby": `${id}-control`,
        "aria-hidden": !isExpanded,
        initial: animations$1.collapsed,
        animate: isExpanded ? animations$1.expanded : animations$1.collapsed
    }, children));
};

const renderContent = (message, type)=>{
    return React.Children.map(message, (m, index)=>/*#__PURE__*/ React.createElement("p", {
            className: "alert__message",
            key: `alert--${type}--${index}`
        }, m)
    );
};
/**
 *
 * @param {'info' | 'success' | 'error' | 'warning'} [type] - Determines which type of alert to render.
 * @param {React.ReactNode} [message] - alert message. If array of elements is passed, each will be wrapped individually in a \<p\> tag.
 * @param {string} [icon] - Which icon to use. Must be a valid icon name for the <Icon /> component.
 * @param {string} [title] - Bolded line of text serving as the alert title. Can only contain plain text.
 * @param {className} [string] - Class name to be added to the root node of the component
 * @param {notifyScreenReader} [boolean] - When 'true', the text within the Alert component will be announced for screenreader users when it is rendered on the page. Use this for dynamic updates, such as error and status messages. Do NOT use it for static content that doesn't change or update. Defaults to false
 * @param {titleHeadingLevel} [string] - Determines which HTML text element is used for the Title section. Defaults to h3
 * @returns
 */ const Alert = (_param)=>{
    var { type ='info' , message , icon , title , children , className , notifyScreenReader , titleHeadingLevel ='h3'  } = _param, attr = swcHelpers.objectWithoutProperties(_param, [
        "type",
        "message",
        "icon",
        "title",
        "children",
        "className",
        "notifyScreenReader",
        "titleHeadingLevel"
    ]);
    if (!children && !message && !title) {
        return null;
    }
    const classes = classnames(`alert`, `alert--${type}`, className, {
        'alert--with-icon': icon
    });
    // If notifyScreenReader is defined, always use that value. Otherwise, check if 'type' equals 'error'
    const shouldNotifySR = notifyScreenReader !== undefined ? notifyScreenReader : type === 'error';
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({}, attr, {
        className: classes,
        "data-t": `alert-${type}`,
        role: shouldNotifySR ? 'alert' : undefined
    }), icon && /*#__PURE__*/ React.createElement(Icon, {
        name: icon,
        className: "alert__icon"
    }), title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "subheading1",
        component: titleHeadingLevel,
        className: "alert__title"
    }, title), message && renderContent(message, type), children && /*#__PURE__*/ React.createElement("div", {
        className: "alert__children"
    }, children));
};

const useKeyPress = function(targetKey, ref2) {
    const [keyPressed, setKeyPressed] = React__default.useState(false);
    const downHandler = ({ code  })=>{
        if (code === targetKey) {
            setKeyPressed(true);
        }
    };
    const upHandler = ({ code  })=>{
        if (code === targetKey) {
            setKeyPressed(false);
        }
    };
    React__default.useEffect(()=>{
        var ref4, ref1;
        (ref4 = ref2.current) === null || ref4 === void 0 ? void 0 : ref4.addEventListener('keydown', downHandler);
        (ref1 = ref2.current) === null || ref1 === void 0 ? void 0 : ref1.addEventListener('keyup', upHandler);
        return ()=>{
            var ref, ref3;
            (ref = ref2.current) === null || ref === void 0 ? void 0 : ref.removeEventListener('keydown', downHandler);
            (ref3 = ref2.current) === null || ref3 === void 0 ? void 0 : ref3.removeEventListener('keyup', upHandler);
        };
    }, []);
    return keyPressed;
};

const useCursor = ({ hasTopContent , onSubmit , onSuggestionClick , onSuggestionTopContentClick , ref , setIsSuggestionsOpen , suggestions  })=>{
    const startCursorValue = 0;
    const [cursor, setCursor] = React__default.useState(-1);
    const [hovered, setHovered] = React__default.useState(undefined);
    const downPress = useKeyPress('ArrowDown', ref);
    const upPress = useKeyPress('ArrowUp', ref);
    const enterPress = useKeyPress('Enter', ref);
    const spacePress = useKeyPress('Space', ref);
    React__default.useEffect(()=>{
        if (!upPress) {
            return;
        }
        if (cursor < 0) {
            setCursor(suggestions.length);
            return;
        }
        setCursor((prevState)=>{
            if (prevState > startCursorValue) {
                return prevState - 1;
            } else {
                setIsSuggestionsOpen(false);
                return -1;
            }
        });
    }, [
        upPress
    ]);
    React__default.useEffect(()=>{
        if (!downPress) {
            return;
        }
        if (cursor < 0) {
            setCursor(startCursorValue);
            return;
        }
        setCursor((prevState)=>{
            const numberOfSuggestions = hasTopContent ? suggestions.length : suggestions.length - 1;
            if (prevState < numberOfSuggestions) {
                return prevState + 1;
            } else {
                setIsSuggestionsOpen(false);
                return -1;
            }
        });
    }, [
        downPress
    ]);
    React__default.useEffect(()=>{
        if (enterPress && cursor === -1 && typeof onSubmit === 'function') {
            onSubmit();
            return;
        }
        if (enterPress && cursor === startCursorValue && typeof onSuggestionTopContentClick === 'function') {
            onSuggestionTopContentClick();
            return;
        }
        const item = hasTopContent ? suggestions[cursor - 1] : suggestions[cursor];
        if (enterPress && suggestions.length && item) {
            onSuggestionClick(item.id);
        }
    }, [
        enterPress
    ]);
    React__default.useEffect(()=>{
        if (spacePress && cursor === startCursorValue && typeof onSuggestionTopContentClick === 'function') {
            onSuggestionTopContentClick();
            return;
        }
        const item = hasTopContent ? suggestions[cursor - 1] : suggestions[cursor];
        if (spacePress && suggestions.length && item) {
            onSuggestionClick(item.id);
        }
    }, [
        spacePress
    ]);
    React__default.useEffect(()=>{
        if (hovered === 'content') {
            setCursor(0);
            return;
        }
        if (hovered === undefined) {
            setCursor(-1);
        }
        if (suggestions.length && hovered) {
            const index = hasTopContent ? 1 : 0;
            setCursor(suggestions.indexOf(hovered) + index);
        }
    }, [
        hovered
    ]);
    React__default.useEffect(()=>{
        setCursor(-1);
    }, [
        suggestions
    ]);
    return React__default.useMemo(()=>({
            cursor,
            setCursor,
            setHovered
        })
    , [
        cursor,
        setCursor,
        setHovered
    ]);
};

const useIsOpenSuggestions = (inputRef)=>{
    const [isSuggestionsOpen, setIsSuggestionsOpen] = React__default.useState(false);
    const escPress = useKeyPress('Escape', inputRef);
    React__default.useEffect(()=>{
        setIsSuggestionsOpen(false);
    }, [
        escPress
    ]);
    return [
        isSuggestionsOpen,
        setIsSuggestionsOpen
    ];
};

const Loader = ({ className  })=>/*#__PURE__*/ React.createElement("div", {
        "aria-hidden": "true",
        className: classnames('loader', className),
        "data-t": "loader"
    })
;

const handleOnClear = (ref, callback)=>{
    var ref1, ref2;
    callback();
    (ref1 = ref) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.current) === null || ref2 === void 0 ? void 0 : ref2.focus();
};
const handleOnSubmit = (ref, callback)=>{
    callback === null || callback === void 0 ? void 0 : callback();
    if (isTouchDevice() === false) {
        var ref3, ref4;
        (ref3 = ref) === null || ref3 === void 0 ? void 0 : (ref4 = ref3.current) === null || ref4 === void 0 ? void 0 : ref4.focus();
    }
};
const Input = /*#__PURE__*/ React__default.forwardRef((_param, ref)=>{
    var { className , count , cursor , disabled , isInvalid , isListSuggestionsOpen , isValid , labels , loading , name , onBlur , onChange , onClear , onClick , onSubmit , placeholder , showClearButton =false , submitIcon , type , value  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "count",
        "cursor",
        "disabled",
        "isInvalid",
        "isListSuggestionsOpen",
        "isValid",
        "labels",
        "loading",
        "name",
        "onBlur",
        "onChange",
        "onClear",
        "onClick",
        "onSubmit",
        "placeholder",
        "showClearButton",
        "submitIcon",
        "type",
        "value"
    ]);
    const status = React__default.useMemo(()=>isListSuggestionsOpen && count > 0 ? `${count} ${labels.availableResultsScreenReaderHint}` : labels.noAvailableResultsScreenReaderHint
    , [
        count,
        labels,
        isListSuggestionsOpen
    ]);
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('autocomplete-input', className)
    }), /*#__PURE__*/ React__default.createElement("span", {
        id: `${name}-autocomplete-description`,
        className: "autocomplete-input__hint"
    }, labels.inputScreenReaderHint), status && /*#__PURE__*/ React__default.createElement("span", {
        role: "status",
        "aria-live": "assertive",
        className: "autocomplete-input__hint--hidden"
    }, status), /*#__PURE__*/ React__default.createElement("input", {
        "aria-activedescendant": cursor < 0 ? undefined : `autocomplete-suggestion-item-${cursor}`,
        "aria-autocomplete": "both",
        "aria-describedby": `${name}-autocomplete-description`,
        "aria-expanded": isListSuggestionsOpen,
        "aria-label": name,
        "aria-owns": `${name}-suggestions`,
        "aria-controls": `${name}-suggestions`,
        autoComplete: "off",
        className: classnames('autocomplete-input__input', {
            'autocomplete-input__input--extra-padding': showClearButton || loading,
            _is_invalid: isInvalid,
            _is_valid: isValid
        }),
        "data-t": name,
        disabled: disabled,
        id: name,
        name: name,
        onBlur: onBlur,
        onChange: onChange,
        onClick: onClick,
        placeholder: placeholder,
        ref: ref,
        role: "combobox",
        type: type,
        value: value,
        "aria-invalid": !isValid
    }), /*#__PURE__*/ React__default.createElement("div", {
        className: "autocomplete-input__button-wrapper"
    }, loading ? /*#__PURE__*/ React__default.createElement(Loader, {
        className: "autocomplete-input__loader"
    }) : showClearButton && /*#__PURE__*/ React__default.createElement("button", {
        "aria-label": labels.clearButtonScreenReaderHint,
        type: "button",
        onClick: ()=>handleOnClear(ref, onClear)
        ,
        className: "autocomplete-input__button autocomplete-input__button--clear"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "clear"
    })), submitIcon && /*#__PURE__*/ React__default.createElement("button", {
        "aria-label": labels.submitButtonScreenReaderHint,
        type: "button",
        onClick: ()=>handleOnSubmit(ref, onSubmit)
        ,
        className: "autocomplete-input__button autocomplete-input__button--submit"
    }, submitIcon)));
});

const SuggestionItem = (_param)=>{
    var { className , active , suggestion , setFocus , onBlur , setHovered , onSuggestionClick  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "active",
        "suggestion",
        "setFocus",
        "onBlur",
        "setHovered",
        "onSuggestionClick"
    ]);
    const { title , subTitle , cursor , id  } = suggestion;
    const onFocus = React__default.useCallback(()=>setFocus(cursor)
    , [
        setFocus,
        cursor
    ]);
    const onMouseEnter = React__default.useCallback(()=>setHovered(suggestion)
    , [
        setHovered,
        suggestion
    ]);
    const onMouseLeave = React__default.useCallback(()=>setHovered(undefined)
    , [
        setHovered
    ]);
    const onClick = React__default.useCallback(()=>onSuggestionClick(id)
    , [
        id,
        onSuggestionClick
    ]);
    return /*#__PURE__*/ React__default.createElement("li", swcHelpers.extends({}, rest, {
        role: "presentation",
        className: classnames('autocomplete-suggestions-item', {
            'autocomplete-suggestions-item--active': active
        }, className)
    }), /*#__PURE__*/ React__default.createElement("button", {
        id: `autocomplete-suggestion-item-${cursor}`,
        role: "option",
        "aria-selected": active,
        onFocus: onFocus,
        onBlur: onBlur,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        onClick: onClick,
        className: classnames('autocomplete-suggestions-item__button', {
            'autocomplete-suggestions-item__button--focused': active
        })
    }, /*#__PURE__*/ React__default.createElement("span", {
        className: "autocomplete-suggestions-item__button--title"
    }, title), subTitle && /*#__PURE__*/ React__default.createElement("span", {
        "data-t": "suggestion-sub-title",
        className: "autocomplete-suggestions-item__button--subtitle"
    }, ' ', subTitle)));
};

const SuggestionTopContent = (_param)=>{
    var { className , cursor , suggestionTopContent , onClick , setHovered , setFocus  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "cursor",
        "suggestionTopContent",
        "onClick",
        "setHovered",
        "setFocus"
    ]);
    const onMouseEnter = React__default.useCallback(()=>setHovered('content')
    , [
        setHovered
    ]);
    const onMouseLeave = React__default.useCallback(()=>setHovered(undefined)
    , [
        setHovered
    ]);
    const onFocus = React__default.useCallback(()=>{
        setFocus(0);
    }, [
        setFocus
    ]);
    const onBlur = React__default.useCallback(()=>{
        setFocus(-1);
    }, [
        setFocus
    ]);
    const onClickHandler = React__default.useCallback((e)=>{
        if (e.currentTarget === document.activeElement && onClick) {
            e.preventDefault();
            onClick(e);
        }
    }, [
        onClick
    ]);
    return /*#__PURE__*/ React__default.createElement("li", swcHelpers.extends({}, rest, {
        role: "presentation",
        className: classnames('suggestion-top-content', {
            'suggestion-top-content--active': cursor === 0
        }, className)
    }), /*#__PURE__*/ React__default.createElement("button", {
        id: "autocomplete-suggestion-item-0",
        role: "option",
        "aria-selected": cursor === 0,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        onFocus: onFocus,
        onBlur: onBlur,
        onClick: onClickHandler,
        className: classnames('suggestion-top-content__button', {
            'suggestion-top-content__button--focused': cursor === 0
        })
    }, suggestionTopContent));
};

const Suggestions = (_param)=> {
    var { autocompleteName , className , cursor , suggestions , suggestionTopContent , noResults , onSuggestionTopContentClick , onSuggestionClick , setHovered , setFocus , onBlur  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "autocompleteName",
        "className",
        "cursor",
        "suggestions",
        "suggestionTopContent",
        "noResults",
        "onSuggestionTopContentClick",
        "onSuggestionClick",
        "setHovered",
        "setFocus",
        "onBlur"
    ]);
    return React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('autocomplete-suggestions', className)
    }), /*#__PURE__*/ React__default.createElement("ul", {
        id: `${autocompleteName}-suggestions`,
        role: "listbox",
        className: "autocomplete-suggestions__list"
    }, suggestionTopContent && /*#__PURE__*/ React__default.createElement(SuggestionTopContent, {
        suggestionTopContent: suggestionTopContent,
        cursor: cursor,
        onClick: onSuggestionTopContentClick,
        setHovered: setHovered,
        setFocus: setFocus
    }), suggestions.length > 0 ? suggestions.map((suggestion)=>/*#__PURE__*/ React__default.createElement(SuggestionItem, {
            onSuggestionClick: onSuggestionClick,
            suggestion: suggestion,
            key: suggestion.id,
            active: cursor === suggestion.cursor,
            setHovered: setHovered,
            setFocus: setFocus,
            onBlur: onBlur,
            "data-t": "autoCompleteSuggestion"
        })
    ) : /*#__PURE__*/ React__default.createElement("div", {
        className: "autocomplete-suggestions__no-result"
    }, noResults)));
};

const assignCursorNumberToSuggestion = (suggestions, hasTopContent)=>suggestions.map((suggestion, index)=>swcHelpers.objectSpread({}, suggestion, {
            cursor: hasTopContent ? index + 1 : index
        })
    )
;
const Autocomplete = (_param)=>{
    var { className , disabled =false , handleClickOutside , isFocusedOnMount , isInvalid , isSuggestionSelected , isValid , labels , loading , minCountCharactersToSubmit =0 , name , onChange , onClear , onSubmit , onSuggestionClick , onSuggestionTopContentClick , setIsTouched , showClearButton , submitIcon , suggestions , suggestionTopContent , type ='text' , value , inputRef =/*#__PURE__*/ React__default.createRef() , inputDataT  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "disabled",
        "handleClickOutside",
        "isFocusedOnMount",
        "isInvalid",
        "isSuggestionSelected",
        "isValid",
        "labels",
        "loading",
        "minCountCharactersToSubmit",
        "name",
        "onChange",
        "onClear",
        "onSubmit",
        "onSuggestionClick",
        "onSuggestionTopContentClick",
        "setIsTouched",
        "showClearButton",
        "submitIcon",
        "suggestions",
        "suggestionTopContent",
        "type",
        "value",
        "inputRef",
        "inputDataT"
    ]);
    const { getLabel  } = useIntl();
    const ref1 = React__default.useRef(null);
    const hasTopContent = Boolean(suggestionTopContent);
    const hasFocusedOnMount = React__default.useRef(false);
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useIsOpenSuggestions(inputRef);
    const suggestionsWithCursor = React__default.useMemo(()=>assignCursorNumberToSuggestion(suggestions, hasTopContent)
    , [
        suggestions,
        hasTopContent
    ]);
    useOnClickOutside(ref1, ()=>{
        setIsSuggestionsOpen(false);
        handleClickOutside && handleClickOutside(value);
    });
    const { cursor , setCursor , setHovered  } = useCursor({
        ref: inputRef,
        suggestions: suggestionsWithCursor,
        hasTopContent,
        onSuggestionClick,
        onSuggestionTopContentClick,
        onSubmit,
        setIsSuggestionsOpen
    });
    const showSuggestions = !isSuggestionSelected ? value.length > minCountCharactersToSubmit || suggestions.length > 0 : false;
    const handleOnClick = React__default.useCallback(()=>{
        setIsSuggestionsOpen(showSuggestions);
    }, [
        showSuggestions,
        setIsSuggestionsOpen
    ]);
    const handleOnChange = React__default.useCallback((event)=>{
        onChange(event);
    }, [
        onChange
    ]);
    const handleOnBlur = React__default.useCallback(()=>{
        if (cursor === (hasTopContent ? suggestions.length : suggestions.length - 1)) {
            setCursor(-1);
            setIsSuggestionsOpen(false);
        }
    }, [
        cursor,
        hasTopContent,
        suggestions.length,
        setCursor,
        setIsSuggestionsOpen
    ]);
    React__default.useEffect(()=>{
        /* do not focus the input field if mobile or tablet, because that will make
     * the virtual keyboard stay which hides the results or other part of the screen */ if (isFocusedOnMount && hasFocusedOnMount.current === false && isTouchDevice() === false) {
            var ref;
            (ref = inputRef.current) === null || ref === void 0 ? void 0 : ref.focus();
            hasFocusedOnMount.current = true;
        }
    }, [
        inputRef,
        isFocusedOnMount
    ]);
    React__default.useEffect(()=>{
        setIsSuggestionsOpen(showSuggestions);
    // Only update isSuggestionsOpen when suggestions or isSuggestionSelected are changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isSuggestionSelected,
        suggestions
    ]);
    var _submitButtonScreenReaderHint;
    const screenReaderLabels = React__default.useMemo(()=>({
            clearButtonScreenReaderHint: getLabel('elements.autocomplete.clear.button'),
            submitButtonScreenReaderHint: (_submitButtonScreenReaderHint = labels.submitButtonScreenReaderHint) !== null && _submitButtonScreenReaderHint !== void 0 ? _submitButtonScreenReaderHint : getLabel('elements.autocomplete.search.button'),
            // noAvailableResultsScreenReaderHint can be set when, for example, the first option is set to Use My Location even when there are no results.
            noAvailableResultsScreenReaderHint: labels.noAvailableResultsScreenReaderHint,
            availableResultsScreenReaderHint: getLabel('elements.autocomplete.availableResultsScreenReaderHint'),
            inputScreenReaderHint: getLabel('elements.autocomplete.inputScreenReaderHint')
        })
    , [
        getLabel,
        labels.noAvailableResultsScreenReaderHint
    ]);
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({
        ref: ref1
    }, rest, {
        className: classnames('autocomplete', className)
    }), /*#__PURE__*/ React__default.createElement(Input, {
        count: suggestionsWithCursor.length,
        cursor: cursor,
        disabled: disabled,
        isInvalid: isInvalid,
        isListSuggestionsOpen: isSuggestionsOpen,
        isValid: isValid,
        labels: screenReaderLabels,
        loading: loading,
        name: name,
        onBlur: setIsTouched,
        onChange: handleOnChange,
        onClear: onClear,
        onClick: handleOnClick,
        onSubmit: onSubmit,
        placeholder: labels.placeholder,
        ref: inputRef,
        showClearButton: showClearButton,
        submitIcon: submitIcon,
        type: type,
        value: value,
        "data-t": inputDataT
    }), isSuggestionsOpen && /*#__PURE__*/ React__default.createElement(Suggestions, {
        autocompleteName: name,
        cursor: cursor,
        noResults: labels.noResults,
        onBlur: handleOnBlur,
        onSuggestionClick: onSuggestionClick,
        onSuggestionTopContentClick: onSuggestionTopContentClick,
        setFocus: setCursor,
        setHovered: setHovered,
        suggestions: suggestionsWithCursor,
        suggestionTopContent: suggestionTopContent
    }));
};

const defaultPoseConfig = {
    exit: {
        opacity: 0
    },
    enter: {
        opacity: 1
    }
};
/**
 * Consuming components that handle <AnimatePresence> themselves should be able
 * to not have Backdrop wrapped in an <AnimatePresence> component.
 */ const Wrapper$2 = ({ animate , children  })=>animate ? /*#__PURE__*/ React.createElement(AnimatePresence, null, children) : /*#__PURE__*/ React.createElement(React.Fragment, null, children)
;
const Backdrop = ({ className , isVisible , onClick , poseConfig =defaultPoseConfig , animate =false ,  })=>/*#__PURE__*/ React.createElement(Wrapper$2, {
        animate: animate
    }, isVisible && /*#__PURE__*/ React.createElement(motion.div, {
        key: "backdrop",
        className: classnames('backdrop', className),
        role: "presentation",
        onClick: onClick,
        variants: poseConfig,
        initial: "exit",
        animate: "enter",
        exit: "exit"
    }))
;

const renderButtonContent = (icon = '', caption)=>/*#__PURE__*/ React.createElement(React.Fragment, null, icon && /*#__PURE__*/ React.createElement("span", {
        className: "button__icon"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: icon
    })), /*#__PURE__*/ React.createElement("span", {
        className: "button__text"
    }, caption), /*#__PURE__*/ React.createElement("div", {
        className: "button__loader-wrapper"
    }, /*#__PURE__*/ React.createElement(Loader, {
        className: "button__loader"
    })))
;
const Button = (_param)=>{
    var { caption , type ='button' , variant ='solid' , size ='medium' , color ='primary' , buttonId , disabled , isLoading , icon , btnRef , href , hrefTarget , className , form , onClick , tabIndex  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "caption",
        "type",
        "variant",
        "size",
        "color",
        "buttonId",
        "disabled",
        "isLoading",
        "icon",
        "btnRef",
        "href",
        "hrefTarget",
        "className",
        "form",
        "onClick",
        "tabIndex"
    ]);
    const classes = classnames(`button button--${variant} button--${size}`, {
        [`button--color-${color}`]: variant !== 'barebones'
    }, className, {
        _is_disabled: disabled,
        _is_loading: isLoading
    });
    if (href) {
        return disabled ? /*#__PURE__*/ React.createElement("div", {
            className: classes,
            "data-t": rest['data-t'],
            style: rest.style
        }, renderButtonContent(icon, caption)) : /*#__PURE__*/ React.createElement("a", {
            className: classes,
            href: href,
            target: hrefTarget,
            rel: hrefTarget === '_blank' ? 'noreferrer noopener' : undefined,
            "aria-busy": isLoading,
            "data-t": rest['data-t'],
            "aria-label": rest['aria-label'],
            "aria-describedby": rest['aria-describedby'],
            onClick: onClick,
            tabIndex: tabIndex,
            style: rest.style
        }, renderButtonContent(icon, caption));
    }
    return(// eslint-disable-next-line jsx-a11y/role-supports-aria-props
    /*#__PURE__*/ React.createElement("button", {
        type: type,
        id: buttonId,
        className: classes,
        onClick: !disabled && onClick || undefined,
        disabled: isLoading || disabled || false,
        // the select service store button is used like a form element
        "aria-invalid": rest['aria-invalid'],
        "aria-label": rest['aria-label'],
        "aria-describedby": rest['aria-describedby'],
        "aria-busy": isLoading,
        "data-t": rest['data-t'],
        form: form,
        ref: btnRef,
        tabIndex: tabIndex,
        style: rest.style
    }, renderButtonContent(icon, caption)));
};

/** Use this component when using multiple CardButtons or other actions */ const CardActions = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React.createElement("div", swcHelpers.extends({
        className: classnames('card-actions', className)
    }, rest), children);
};

const CardButton = (_param)=>{
    var { 'data-t': dataT , onClick , children  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        'data-t',
        "onClick",
        "children"
    ]);
    return /*#__PURE__*/ React.createElement(Button$1, swcHelpers.extends({
        "data-t": dataT || 'card-button',
        className: "card-button",
        onClick: onClick,
        variant: "ghost"
    }, rest), children);
};

/**
 * This component can hold one or more components, usually buttons or links, and will
 * make sure they are consistently laid out. It puts them on single line. They will be
 * spaced correcly. If they don't fit, they will wrap and still be spaced correctly.
 */ function CardButtonLayout(_param) {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({
        className: classnames('card-button-layout', className)
    }, rest), children);
}

const CardIcon = ({ className , name , height , width  })=>/*#__PURE__*/ React.createElement(Icon, {
        className: classnames('card-icon', className),
        height: height,
        width: width,
        name: name
    })
;

/** Adds padding to the card */ const CardContent = (_param)=> {
    var { children , variant ='normal' , center , className , childrenWrapperClassName  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "variant",
        "center",
        "className",
        "childrenWrapperClassName"
    ]);
    return React.createElement("div", swcHelpers.extends({
        className: classnames('card-content', `card-content--${variant}`, className, {
            'card-content--center': center
        })
    }, rest), /*#__PURE__*/ React.createElement("div", {
        className: childrenWrapperClassName
    }, children), variant === 'link' ? /*#__PURE__*/ React.createElement(CardIcon, {
        name: "chevron-right",
        className: "card-content__link-icon"
    }) : null);
};

const CardHeading = (_param)=> {
    var { className , variant ='smallest' , children  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "variant",
        "children"
    ]);
    return React.createElement(Heading, swcHelpers.extends({
        variant: variant,
        className: classnames('card-heading', className)
    }, rest), children);
};

const CardImage = (_param)=> {
    var { alt , src , backgroundColor , className , style  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "alt",
        "src",
        "backgroundColor",
        "className",
        "style"
    ]);
    return React.createElement("img", swcHelpers.extends({}, rest, {
        className: classnames('card-image', className, {
            'card-image--placeholder': !src
        }),
        // Even though it's required, the src might be an empty string.
        src: src || placeholderImage.url,
        alt: alt || '',
        style: swcHelpers.objectSpread({
            backgroundColor
        }, style)
    }));
};

/**
 * This component is essentially the same as "elements/Text", but it
 * sets the margin to 0.Once we've gotten rid of all of the margins
 * on our components, this component will be obsolete.
 */ const CardText = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React.createElement(Text, swcHelpers.extends({
        className: classnames('card-text', className)
    }, rest), children);
};

const CardLink = (_param)=> {
    var { children , className , target , variant ='body-head' , component ='span'  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className",
        "target",
        "variant",
        "component"
    ]);
    return React.createElement(CardText, {
        component: component,
        variant: variant
    }, /*#__PURE__*/ React.createElement(Link, swcHelpers.extends({
        className: classnames('card-link', className)
    }, rest), children));
};

function CardSection(_param) {
    var { children , flex , className , spacing ='l'  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "flex",
        "className",
        "spacing"
    ]);
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({
        className: classnames('card-section', `card-section--spacing-${spacing}`, className, {
            'card-section--flex': flex
        })
    }, rest), children);
}

/**
 * @deprecated This component should be replaced with Card when the design is aligned.
 */ const CardWrapper = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React.createElement("div", {
        "data-t": rest['data-t'],
        className: classnames('card', className)
    }, children);
};

const Wrapper$1 = (props)=>'href' in props ? /*#__PURE__*/ React.createElement("a", swcHelpers.extends({}, props), props.children) : /*#__PURE__*/ React.createElement("div", swcHelpers.extends({}, props))
;
/** Base component for constructing a layered card */ const Card = (_param)=>{
    var { layout ='vertical' , children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "layout",
        "children",
        "className"
    ]);
    const isLink = 'href' in rest;
    return /*#__PURE__*/ React.createElement(Wrapper$1, swcHelpers.extends({
        className: classnames('card-base', `card-base--${layout}`, className, {
            'card-base--with-link': isLink
        })
    }, rest), React.Children.map(children, (child)=>{
        if (!/*#__PURE__*/ React.isValidElement(child)) return child;
        /**
         * Need to add class to CardImage so Card can target it without reaching
         * inside of CardImage to know its root class.
         */ if (child.type === CardImage) {
            return /*#__PURE__*/ React.createElement(CardImage, swcHelpers.extends({}, child.props, {
                className: classnames(child.props.className, 'card-base__image')
            }));
        }
        /**
         * Render "link" variant of CardContent if Card is rendering as a link.
         * This will show the chevron in CardContent, indicating that the entire
         * card can be clicked and functions as a link.
         */ if (isLink && child.type === CardContent) {
            return /*#__PURE__*/ React.createElement(CardContent, swcHelpers.extends({}, child.props, {
                variant: "link"
            }));
        }
        return child;
    }));
};
Card.Actions = CardActions;
Card.Button = CardButton;
Card.ButtonLayout = CardButtonLayout;
Card.Content = CardContent;
Card.Heading = CardHeading;
Card.Icon = CardIcon;
Card.Image = CardImage;
Card.Link = CardLink;
Card.Section = CardSection;
Card.Text = CardText;
Card.OldCard = CardWrapper;

const INTERVAL = 4000;
const Carousel = ({ children , className , autoLoop =false , navigation =true , pagination =true , inverseColor =false , onPrevSlide =undefined , onNextSlide =undefined , 'data-t': dataT ,  })=>{
    const { getLabel  } = useIntl();
    const [activeIndex, setActiveIndex] = React.useState(0);
    const setSlide = React.useCallback((index)=>{
        if (index !== activeIndex) {
            setActiveIndex(index);
        }
    }, [
        activeIndex
    ]);
    const prevSlide = React.useCallback(()=>{
        setActiveIndex((index)=>index !== 0 ? index - 1 : children.length
        );
    }, [
        children.length
    ]);
    const nextSlide = React.useCallback(()=>{
        setActiveIndex((index)=>index !== children.length - 1 ? index + 1 : 0
        );
    }, [
        children.length
    ]);
    const handlePrevClick = React.useCallback((e)=>{
        prevSlide();
        e.preventDefault();
        onPrevSlide && onPrevSlide();
    }, [
        onPrevSlide,
        prevSlide
    ]);
    const handleNextClick = React.useCallback((e)=>{
        nextSlide();
        e.preventDefault();
        onNextSlide && onNextSlide();
    }, [
        nextSlide,
        onNextSlide
    ]);
    useInterval(nextSlide, autoLoop ? INTERVAL : null);
    if (!children || children.length === 0) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('carousel', className),
        "data-t": dataT || 'carousel'
    }, /*#__PURE__*/ React.createElement("div", {
        className: "carousel__inner"
    }, /*#__PURE__*/ React.createElement(SwipeableViews, {
        style: {
            height: '100%'
        },
        onChangeIndex: setSlide,
        enableMouseEvents: true,
        index: activeIndex
    }, children.map((child, i)=>/*#__PURE__*/ React.createElement("div", {
            className: "carousel__item",
            key: `item-${i}`
        }, child)
    ))), navigation && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("button", {
        className: classnames('carousel__navigation-chevron carousel__navigation-chevron--left', {
            disabled: activeIndex < 1,
            inversed: inverseColor
        }),
        disabled: activeIndex < 1,
        onClick: handlePrevClick,
        "aria-label": getLabel('common.actions.previous')
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-left"
    })), /*#__PURE__*/ React.createElement("button", {
        className: classnames('carousel__navigation-chevron carousel__navigation-chevron--right', {
            disabled: activeIndex >= children.length - 1,
            inversed: inverseColor
        }),
        disabled: activeIndex >= children.length - 1,
        onClick: handleNextClick,
        "aria-label": getLabel('common.actions.next')
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-right"
    }))), pagination && /*#__PURE__*/ React.createElement("div", {
        className: "carousel__pagination"
    }, children.map((_, index)=>/*#__PURE__*/ React.createElement("button", {
            key: index,
            "aria-label": `${index + 1}`,
            className: classnames('carousel__pagination-indicator', {
                _is_active: index === activeIndex
            }),
            onClick: ()=>setSlide(index)
        })
    )));
};

const CartCounter = ({ count  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Icon, {
        name: "shopping-bag"
    }), count > 0 && /*#__PURE__*/ React.createElement("span", {
        className: "cart-counter"
    }, count))
;

class CartButton extends (React.Component) {
    static getCartCount() {
        const fromStorage = window.localStorage.getItem('stored_cart');
        if (!fromStorage) {
            return 0;
        }
        const cart = JSON.parse(fromStorage);
        return cart && cart.cartId ? cart.cartCount : 0;
    }
    componentDidMount() {
        // Get and set count on load
        const count = CartButton.getCartCount();
        this.setState({
            count
        });
        // update cart count in current window and other windows
        window.addEventListener('cart.change', this.onStoredCartChange);
        window.addEventListener('storage', this.onStoredCartChange);
    }
    componentWillUnmount() {
        window.removeEventListener('storage', this.onStoredCartChange);
        window.removeEventListener('cart.change', this.onStoredCartChange);
    }
    render() {
        const { count  } = this.state;
        const { href , className , intl , label , tabIndex  } = this.props;
        const ariaLabel = count === 0 ? intl.getLabel('cart.emptyCart.title') : getCartTotalsTitle(count, intl);
        return /*#__PURE__*/ React.createElement("a", {
            className: `cart-button ${className || ''}`,
            href: href,
            title: label,
            tabIndex: tabIndex,
            "aria-label": tabIndex === -1 ? '' : ariaLabel
        }, /*#__PURE__*/ React.createElement(CartCounter, {
            count: count
        }), label && /*#__PURE__*/ React.createElement("span", {
            className: "cart-button__label"
        }, label));
    }
    constructor(...args){
        super(...args);
        this.state = {
            count: 0
        };
        this.onStoredCartChange = ()=>{
            const count = CartButton.getCartCount();
            this.setState({
                count
            });
        };
    }
}
CartButton.defaultProps = {
    href: '/cart',
    tabIndex: undefined
};
var CartButton$1 = withIntl(CartButton);

/**
 * @deprecated -
 * Use the new CheckboxList from the Form package.
 * That one doesn't uses the deprecated Checkbox.
 */ const CheckboxList = function({ idPrefix , items , selectedItems , getItemIdentifier , renderItem , renderTrailingItem , onChange , isItemDisabled , checkboxAlignment ='top' , className , itemClassName  }) {
    const handleChange = React.useCallback(({ value , checked  })=>{
        if (!onChange) {
            return;
        }
        const newValues = checked ? [
            ...selectedItems,
            value
        ] : selectedItems.filter((i)=>getItemIdentifier(i) !== getItemIdentifier(value)
        );
        onChange(newValues);
    }, [
        selectedItems,
        getItemIdentifier,
        onChange
    ]);
    return /*#__PURE__*/ React.createElement("fieldset", {
        className: classnames('checkbox-list', className)
    }, items.map((item)=>{
        const id = idPrefix + String(getItemIdentifier(item));
        const isSelected = selectedItems.some((i)=>getItemIdentifier(i) === getItemIdentifier(item)
        );
        const isDisabled = isItemDisabled === null || isItemDisabled === void 0 ? void 0 : isItemDisabled(item);
        const trailingMarkup = renderTrailingItem === null || renderTrailingItem === void 0 ? void 0 : renderTrailingItem(item);
        return /*#__PURE__*/ React.createElement("div", {
            key: id,
            className: classnames('checkbox-list__item', itemClassName)
        }, /*#__PURE__*/ React.createElement("div", {
            className: `checkbox-list__item-checkbox checkbox-list__item-checkbox--${checkboxAlignment}`
        }, /*#__PURE__*/ React.createElement(Checkbox, {
            id: id,
            value: item,
            checked: isSelected,
            disabled: isDisabled,
            onChange: handleChange
        })), /*#__PURE__*/ React.createElement("div", {
            className: "checkbox-list__item-content"
        }, /*#__PURE__*/ React.createElement("label", {
            className: "checkbox-list__item-label",
            htmlFor: id
        }, renderItem(item)), trailingMarkup && /*#__PURE__*/ React.createElement("span", {
            className: "checkbox-list__item-trailing"
        }, trailingMarkup)));
    }));
};

const TabLink = /*#__PURE__*/ React.forwardRef((_param, ref)=> {
    var { isActive , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "isActive",
        "className"
    ]);
    return React.createElement(Link, swcHelpers.extends({}, rest, {
        ref: ref,
        className: classnames('tab', className, {
            _is_active: isActive
        })
    }));
});
TabLink.displayName = 'TabLink';

const setElementScrollLeft = (element, value)=>{
    element.scrollLeft = value;
};
const Tabs = ({ children , onChange , activeTabIndex , variant ='primary'  })=>{
    const tabsRef = useRef(null);
    const activeTabRef = useRef(null);
    const [isScrolling, setIsScrolling] = useState(false);
    useEffect(()=>{
        const setScrolling = (current)=>{
            const { offsetWidth , scrollWidth  } = current;
            offsetWidth < scrollWidth ? setIsScrolling(true) : setIsScrolling(false);
        };
        const onResize = ()=>{
            setScrolling(tabsRef.current);
        };
        if (tabsRef.current) {
            setScrolling(tabsRef.current);
            window.addEventListener('resize', onResize);
            activeTabRef.current && setElementScrollLeft(tabsRef.current, activeTabRef.current.offsetLeft);
        }
        return ()=>window.removeEventListener('resize', onResize)
        ;
    }, [
        tabsRef.current
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "tabs__wrapper"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: classnames(`tabs tabs--${variant}`, {
            'tabs--scrolling': isScrolling
        }),
        ref: tabsRef
    }, React__default.Children.map(children, (child, index)=>{
        if (/*#__PURE__*/ React__default.isValidElement(child)) {
            return /*#__PURE__*/ React__default.cloneElement(child, {
                onClick: ()=>{
                    onChange && onChange(index);
                    child.props.onClick && child.props.onClick();
                },
                isActive: activeTabIndex === index,
                variant,
                ref: activeTabIndex === index ? activeTabRef : undefined
            });
        }
    }), isScrolling && /*#__PURE__*/ React__default.createElement(Icon, {
        className: "tabs__scroll-icon",
        name: "chevron-right"
    })));
};

const ContentTabs = ({ links  })=>{
    const [activeTabIndex, setActiveTabIndex] = useState(-1);
    useEffect(()=>{
        if (typeof window !== 'undefined' && links && links.length) {
            const path = window.location.pathname;
            const tabIndex = links.findIndex(({ url  })=>url === path
            );
            setActiveTabIndex(tabIndex);
        }
    }, [
        links
    ]);
    return links && links.length ? /*#__PURE__*/ React__default.createElement(Tabs, {
        activeTabIndex: activeTabIndex
    }, links.map(({ label , url , openInNewWindow  })=>label && url && /*#__PURE__*/ React__default.createElement(TabLink, {
            key: url,
            url: url,
            label: label,
            openInNewWindow: openInNewWindow
        })
    )) : null;
};

/**
 * This component can be use to catch any errors rendered from the subtree. Use as following:
 * <ErrorBoundary>
 *   {(error, info) =>
 *     error ? (
 *       <Heading>There was an error</Heading>
 *      ) : (
 *        <YourPossiblyFailingComponent />
 *      )
 *    }
 * </ErrorBoundary>
 */ class ErrorBoundary extends React__default.Component {
    static getDerivedStateFromError(error) {
        return {
            error
        };
    }
    componentDidCatch(error, errorInfo) {
        this.setState({
            error,
            errorInfo
        });
    }
    render() {
        const { error , errorInfo  } = this.state;
        return this.props.children(error, errorInfo);
    }
    constructor(...args){
        super(...args);
        this.state = {
            error: undefined,
            errorInfo: undefined
        };
    }
}

/**
 * Note: Went for a plural name, because FileList is a DOM type.
 */ const FilesList = ({ items , className  })=>{
    /**
   * No reason to return a <ul> if there are no items to be rendered.
   */ if (!items.length) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("ul", {
        className: classnames('files-list', className)
    }, items.map((item, index)=>/*#__PURE__*/ React.createElement("li", {
            className: "files-list__item",
            key: `${item}-${index}`
        }, /*#__PURE__*/ React.createElement(Icon, {
            name: "paperclip",
            width: 16,
            height: 16,
            className: "files-list__item-icon"
        }), /*#__PURE__*/ React.createElement(Text, {
            variant: "caption",
            className: "files-list__item-text"
        }, item))
    ));
};

const IconLink = ({ className , href , iconName , onClick , children  })=>/*#__PURE__*/ React.createElement("a", {
        href: href,
        className: classnames('icon-link', className),
        onClick: onClick
    }, /*#__PURE__*/ React.createElement(Icon, {
        className: "icon-link__icon",
        name: iconName
    }), children)
;

const Column = (_)=>null
;
Column.displayName = 'Column';
const Footer = (_)=>null
;
Footer.displayName = 'Footer';
const defaultRowKeyFormatter = (rowIndex)=>rowIndex.toString()
;
const Table = ({ caption , children , numberOfRows , 'aria-level': ariaLevel , rowKeyFormatter =defaultRowKeyFormatter  })=>{
    const currentBreakpoint = useBreakpoint();
    if (!children) {
        console.warn(`<DataTable /> component needs a column definition to work`);
        return null;
    }
    const isMobile = currentBreakpoint === BreakpointName.TABLET_PORTRAIT || currentBreakpoint === BreakpointName.PHONE;
    const { columns , footer  } = React.Children.toArray(children).reduce((result, child)=>{
        if (!/*#__PURE__*/ React.isValidElement(child)) {
            // Ignoring all non <Column /> or <Footer /> children
            return result;
        }
        switch(child.type){
            /**
         * Parse props from columns, set renderer to child function
         */ case Column:
                return {
                    columns: [
                        ...result.columns,
                        {
                            renderer: child.props.children,
                            header: child.props.header,
                            key: child.props.id || child.props.header
                        }, 
                    ],
                    footer: result.footer
                };
            /**
         * Set footer to children of <Footer /> element
         */ case Footer:
                return swcHelpers.objectSpread({}, result, {
                    footer: child.props.children
                });
            default:
                return result;
        }
    }, {
        columns: [],
        footer: null
    });
    return /*#__PURE__*/ React.createElement("div", {
        className: "data-table"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        "aria-level": ariaLevel
    }, caption), /*#__PURE__*/ React.createElement("div", {
        className: "data-table__wrapper"
    }, isMobile ? /*#__PURE__*/ React.createElement("ul", {
        "aria-label": caption
    }, Array.from({
        length: numberOfRows
    }).map((_, index)=>/*#__PURE__*/ React.createElement("li", {
            key: rowKeyFormatter(index)
        }, /*#__PURE__*/ React.createElement("dl", null, columns.map((c)=>/*#__PURE__*/ React.createElement(React.Fragment, {
                key: c.key
            }, /*#__PURE__*/ React.createElement("dt", null, c.header), /*#__PURE__*/ React.createElement("dd", null, c.renderer(index)))
        )))
    )) : /*#__PURE__*/ React.createElement("table", {
        "aria-label": caption
    }, /*#__PURE__*/ React.createElement("thead", null, /*#__PURE__*/ React.createElement("tr", null, columns.map((c)=>/*#__PURE__*/ React.createElement("th", {
            scope: "col",
            key: c.key
        }, c.header)
    ))), /*#__PURE__*/ React.createElement("tbody", null, Array.from({
        length: numberOfRows
    }).map((_, index)=>/*#__PURE__*/ React.createElement("tr", {
            key: rowKeyFormatter(index)
        }, columns.map((c)=>/*#__PURE__*/ React.createElement("td", {
                key: c.key
            }, c.renderer(index))
        ))
    ))), footer && /*#__PURE__*/ React.createElement("div", {
        className: "data-table__footer"
    }, footer)));
};
Table.Col = Column;
Table.Footer = Footer;

const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
};
const DateTime = (_param)=>{
    var { dateTime  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "dateTime"
    ]);
    const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
    let { locale  } = rest;
    if (locale) {
        // Browsers require BCP 47 language tag format (see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument)
        locale = locale.replace('_', '-');
    }
    return /*#__PURE__*/ React.createElement("time", {
        dateTime: date.toISOString()
    }, date.toLocaleDateString(locale, options));
};

const DiscountBar = ({ title  })=>/*#__PURE__*/ React.createElement("div", {
        className: "discount-bar"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "discount-bar__title"
    }, title))
;

const Expandable = ({ initialHeight =0 , showLessLabel , showMoreLabel , className , children , id ,  })=>{
    const currentBreakpoint = useBreakpoint();
    const contentContainerRef = React.useRef(null);
    const shouldUpdateToggleRef = React.useRef(true);
    const [isExpanded1, setIsExpanded] = React.useReducer((isExpanded)=>!isExpanded
    , false);
    const [showButton, setShowButton] = React.useState(true);
    React.useEffect(()=>{
        // Keeps record of the expanded state without triggering recalculation
        shouldUpdateToggleRef.current = !isExpanded1;
    }, [
        isExpanded1
    ]);
    /**
   * This effect checks if we should render the button
   * It runs whenever the breakpoint changes.
   * It will not update when the content is expanded because it breaks the calculation
   */ React.useEffect(()=>{
        if (!shouldUpdateToggleRef.current || !contentContainerRef.current) {
            return;
        }
        const contentContainer = contentContainerRef.current;
        const { height  } = contentContainer.getBoundingClientRect();
        setShowButton(contentContainer.scrollHeight > Math.ceil(height));
    }, [
        contentContainerRef,
        shouldUpdateToggleRef,
        currentBreakpoint
    ]);
    if (!children) {
        return null;
    }
    const toggleText = isExpanded1 ? showLessLabel : showMoreLabel;
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('expandable', className, {
            _is_expanded: isExpanded1,
            'expandable--with-button': showButton
        })
    }, /*#__PURE__*/ React.createElement("div", {
        id: `expandable-${id}`,
        className: "expandable__body",
        style: {
            maxHeight: `${initialHeight}px`
        },
        ref: contentContainerRef
    }, children), showButton && /*#__PURE__*/ React.createElement(Button, {
        variant: "ghost",
        onClick: setIsExpanded,
        "aria-expanded": isExpanded1,
        "aria-controls": `expandable-${id}`,
        caption: toggleText,
        size: "custom"
    }));
};

const animations = {
    expanded: {
        height: 'auto',
        overflow: 'hidden',
        transition: {
            ease: 'easeOut',
            duration: 0.3
        }
    },
    collapsed: {
        height: '0px',
        overflow: 'hidden',
        transition: {
            ease: 'easeIn',
            duration: 0.3
        }
    }
};
const Expander = ({ children , className , id , showMoreLabel , showLessLabel , onExpandCallback , isInitiallyExpanded =false  })=>{
    const [isExpanded1, toggleIsExpanded] = React.useReducer((isExpanded)=>!isExpanded
    , isInitiallyExpanded);
    React.useEffect(()=>{
        onExpandCallback(isExpanded1);
    }, [
        isExpanded1,
        onExpandCallback
    ]);
    const toggleText = isExpanded1 ? showLessLabel : showMoreLabel;
    const toggleIcon = isExpanded1 ? 'minus' : 'plus';
    if (!children) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "expander",
        "data-t": "expander"
    }, /*#__PURE__*/ React.createElement(motion.div, {
        id: `expander-${id}`,
        "data-t": "motion-expander",
        initial: animations.collapsed,
        animate: isExpanded1 ? animations.expanded : animations.collapsed,
        "aria-hidden": !isExpanded1,
        className: className
    }, children), /*#__PURE__*/ React.createElement(Button, {
        className: "expander__toggle",
        variant: "outline",
        onClick: toggleIsExpanded,
        "aria-expanded": isExpanded1,
        "aria-controls": `expander-${id}`,
        caption: toggleText,
        icon: toggleIcon
    }));
};

const EyeSideIndicator = ({ className , side  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames(`eyeside-indicator`, `eyeside-indicator--${side}`, className)
    }, getLabel(`common.${side}.abbr`));
};

const FeatureGroupItem = ({ id , icon , link  })=>/*#__PURE__*/ React.createElement("li", {
        className: "feature-group-item"
    }, /*#__PURE__*/ React.createElement("a", {
        href: link.url,
        className: "feature-group-item__box"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "feature-group-item__imagebox"
    }, /*#__PURE__*/ React.createElement(Icon, {
        className: "feature-group-item__icon",
        name: icon
    })), link.label && /*#__PURE__*/ React.createElement("span", {
        className: "feature-group-item__title"
    }, link.label)))
;
const FeatureGroup = ({ items  })=>{
    if (!items) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "feature-group"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "site-container"
    }, /*#__PURE__*/ React.createElement("ul", {
        className: "feature-group__list"
    }, items && items.map((item)=>/*#__PURE__*/ React.createElement(FeatureGroupItem, swcHelpers.extends({
            key: item.id
        }, item))
    ))));
};

const transition = {
    ease: 'easeOut',
    duration: 0.15
};
const variants$2 = {
    enter: {
        y: 0,
        opacity: 1,
        transition
    },
    exit: {
        y: -5,
        opacity: 0,
        transition
    }
};
const FlyoutMenu = (_param)=> {
    var { className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className"
    ]);
    return React__default.createElement(motion.ul, swcHelpers.extends({}, rest, {
        role: "menu",
        className: classnames(`flyout-menu`, className),
        variants: variants$2,
        initial: "exit",
        animate: "enter",
        exit: "exit"
    }));
};

const FlyoutMenuItem = (_param)=> {
    var { link , action , children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "link",
        "action",
        "children",
        "className"
    ]);
    return React__default.createElement("li", swcHelpers.extends({
        className: classnames('flyout-menu-item', className)
    }, rest), !action && link && /*#__PURE__*/ React__default.createElement("a", {
        href: link,
        className: "flyout-menu-item__link"
    }, children), action && /*#__PURE__*/ React__default.createElement("button", {
        onClick: action,
        className: "flyout-menu-item__link"
    }, children));
};

const FlyoutTrigger = (_param)=>{
    var { children , className , isMenuOpen , title  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className",
        "isMenuOpen",
        "title"
    ]);
    const classNames = classnames('flyout-trigger', className, {
        'flyout-trigger--menu-open': isMenuOpen
    });
    return /*#__PURE__*/ React__default.createElement("button", swcHelpers.extends({}, rest, {
        "aria-label": title,
        "aria-expanded": isMenuOpen,
        "aria-haspopup": true,
        className: classNames
    }), children, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "chevron-down",
        className: "flyout-trigger__icon"
    }));
};

/**
 * This function takes inspiration from here:
 * https://github.com/Andarist/use-onclickoutside/blob/master/src/index.ts
 */ function useOnKeyboardEventOutside(ref, handler) {
    React__default.useEffect(()=>{
        if (!handler || !document) {
            return;
        }
        const listener = (event)=>{
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener('keyup', listener);
        return ()=>{
            document.removeEventListener('keyup', listener);
        };
    }, [
        !handler
    ]);
}

const Flyout = (_param)=>{
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    const [isOpen, setIsOpen] = React.useState(false);
    const rootRef = React.useRef(null);
    useOnClickOutside(rootRef, ()=>{
        setIsOpen(false);
    });
    useOnKeyboardEventOutside(rootRef, (event)=>{
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            setIsOpen(false);
        }
    });
    const keyUp = React.useCallback((event)=>{
        if (event.key === 'Escape') {
            setIsOpen(false);
            event.stopPropagation();
        }
    }, [
        setIsOpen
    ]);
    const handleOnClick = React.useCallback(()=>{
        setIsOpen(!isOpen);
    }, [
        isOpen,
        setIsOpen
    ]);
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({
        className: classnames('flyout', className),
        ref: rootRef
    }, rest), /*#__PURE__*/ React.createElement(AnimatePresence, null, React.Children.map(children, (child)=>{
        if (!/*#__PURE__*/ React.isValidElement(child)) {
            return;
        }
        if (child.type === FlyoutTrigger) {
            return /*#__PURE__*/ React.createElement(FlyoutTrigger, swcHelpers.extends({
                key: "trigger"
            }, child.props, {
                onClick: handleOnClick,
                isMenuOpen: isOpen,
                onKeyUp: keyUp
            }));
        }
        if (child.type === FlyoutMenu) {
            return isOpen && /*#__PURE__*/ React.createElement(FlyoutMenu, swcHelpers.extends({
                key: "menu"
            }, child.props, {
                onKeyUp: keyUp
            }));
        }
        if (process.env.NODE_ENV !== 'production') {
            console.error('Flyout only accepts Flyout.Trigger and Flyout.Menu as its children');
        }
    })));
};
Flyout.Trigger = FlyoutTrigger;
Flyout.Menu = FlyoutMenu;
Flyout.MenuItem = FlyoutMenuItem;

function OptionCardGroup(props) {
    const { items , onSelect , bodyRenderer , className , selected , groupName  } = props;
    const rootClassNames = classnames('option-card-group', className);
    const itemClassNames = classnames('option-card-group__item', {
        [`${className}__item`]: className
    });
    const radioClassNames = classnames('option-card-group__radio', {
        [`${className}__radio`]: className
    });
    const inputClassNames = classnames('option-card-group__input', {
        [`${className}__input`]: className
    });
    if (items.length === 0) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement("ul", {
        className: rootClassNames,
        "data-t": props['data-t'],
        role: "radiogroup"
    }, items.map((item)=>{
        const isSelected = selected && item.id === selected.id;
        return /*#__PURE__*/ React__default.createElement("li", {
            className: itemClassNames,
            key: item.id
        }, /*#__PURE__*/ React__default.createElement("label", {
            className: classnames(radioClassNames, {
                'option-card-group__radio--disabled': item.disabled,
                'option-card-group__radio--checked': isSelected
            })
        }, /*#__PURE__*/ React__default.createElement("input", {
            className: inputClassNames,
            name: groupName,
            type: "radio",
            value: item.id,
            checked: isSelected,
            disabled: item.disabled,
            onChange: ()=>onSelect(item)
            ,
            "data-t": item.id
        }), bodyRenderer(item)));
    }));
}

const PreloadFonts = ({ assetsUrl , fonts  })=>{
    if (!assetsUrl) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, fonts.map((font)=>/*#__PURE__*/ React.createElement("link", {
            key: font,
            rel: "preload",
            as: "font",
            href: `${assetsUrl}/${font}.woff2`,
            type: "font/woff2",
            crossOrigin: "anonymous"
        })
    ));
};

const GoogleMapsContextDefaultValues = {
    googleMapsSettings: {
        apiKey: '',
        defaultCenter: {
            lat: 0,
            lng: 0
        },
        defaultZoom: 0
    }
};
const GoogleMapsContext = /*#__PURE__*/ React__default.createContext(GoogleMapsContextDefaultValues);
const useGoogleMapsContext = ()=>React__default.useContext(GoogleMapsContext)
;
function GoogleMapsProvider({ settings , children  }) {
    return /*#__PURE__*/ React__default.createElement(GoogleMapsContext.Provider, {
        value: {
            googleMapsSettings: swcHelpers.objectSpread({}, settings, {
                defaultZoom: settings.defaultZoom,
                hasCustomZoomLevel: settings.hasCustomZoomLevel
            })
        }
    }, children);
}
function withGoogleMaps(Component) {
    const wrappedName = Component.displayName || Component.name || 'Component';
    const WithGoogleMaps = (props)=>/*#__PURE__*/ React__default.createElement(GoogleMapsContext.Consumer, null, (context)=>/*#__PURE__*/ React__default.createElement(Component, swcHelpers.extends({}, props, {
                settings: context
            }))
        )
    ;
    WithGoogleMaps.displayName = `WithGoogleMaps(${wrappedName})`;
    return WithGoogleMaps;
}

const getIconAsSvgString$1 = (width, height, theme)=>`<svg width="${width}" height="${height}" viewBox="0 0 40 47" fill="none" xmlns="http://www.w3.org/2000/svg">
  <ellipse opacity="0.4" cx="20" cy="40.5" rx="5.36842" ry="1" fill="black"/>
  <g filter="url(#shadow)">
    <path d="M21.4203 38.4563C23.0172 36.7144 26.0138 33.1289 28.6184 29.1361C29.921 27.1391 31.1387 25.0205 32.0337 22.9652C32.9226 20.924 33.5263 18.871 33.5263 17.0263C33.5263 9.30363 27.4578 3.5 20 3.5C12.5422 3.5 6.47369 9.30363 6.47369 17.0263C6.47369 18.871 7.07747 20.924 7.96632 22.9652C8.86131 25.0205 10.079 27.1391 11.3817 29.1361C13.9863 33.1289 16.9828 36.7144 18.5797 38.4563C19.3493 39.2957 20.6507 39.2958 21.4203 38.4563Z" fill="${theme.background}" stroke="${theme.foreground}" stroke-width="2"/>
  </g>
  <circle cx="20" cy="17.8684" r="5.36842" fill="${theme.foreground}"/>
  <defs>
  <filter id="shadow" x="1.47369" y="0.5" width="37.0526" height="45.5859" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
  <feOffset dy="2"/>
  <feGaussianBlur stdDeviation="2"/>
  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_19113_15831"/>
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
  <feOffset/>
  <feGaussianBlur stdDeviation="1"/>
  <feComposite in2="hardAlpha" operator="out"/>
  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
  <feBlend mode="normal" in2="effect1_dropShadow_19113_15831" result="effect2_dropShadow_19113_15831"/>
  <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_19113_15831" result="shape"/>
  </filter>
  </defs>
</svg>`
;

const fallbackThemes = {
    inactive: {
        foreground: '#fff',
        background: '#6e757d'
    },
    inactiveHighlight: {
        foreground: '#fff',
        background: '#2B2E31'
    }
};
const Pin = ({ width: defaultWidth = 40 , height: defaultHeight = 47 , themes  })=>{
    return (props)=>{
        const { primary: primaryTheme , highlight: highlightTheme , hovered: hoveredTheme  } = themes;
        const { highlight =false , hovered =false , inactive =false  } = props;
        let theme = primaryTheme;
        if (inactive) {
            if (highlight) {
                theme = (themes === null || themes === void 0 ? void 0 : themes.inactiveHighlight) || fallbackThemes.inactiveHighlight;
            } else {
                theme = (themes === null || themes === void 0 ? void 0 : themes.inactive) || fallbackThemes.inactive;
            }
        } else if (highlight && highlightTheme) {
            theme = highlightTheme;
        } else if (hovered && hoveredTheme) {
            theme = hoveredTheme;
        }
        const width = theme.width || defaultWidth;
        const height = theme.height || defaultHeight;
        const anchor = {
            x: Math.round(width / 2),
            y: height
        };
        const size = {
            width,
            height
        };
        const svgString = getIconAsSvgString$1(width, height, theme);
        return {
            url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`,
            anchor,
            size,
            scaledSize: size
        };
    };
};

const LoaderScreen = ({ height =75 , delay , className  })=>{
    const [hasTimeoutPassed, setHasTimeoutPassed] = React.useState(true);
    const intl = useIntl();
    React.useEffect(()=>{
        if (delay) {
            setHasTimeoutPassed(false);
            const timeout = setTimeout(()=>{
                setHasTimeoutPassed(true);
            }, delay);
            return ()=>clearTimeout(timeout)
            ;
        }
    }, [
        delay
    ]);
    return hasTimeoutPassed ? /*#__PURE__*/ React.createElement("div", {
        "data-t": "loader-screen",
        className: classnames('loader-screen', className),
        style: {
            height: `${height}vh`
        },
        "aria-busy": true
    }, /*#__PURE__*/ React.createElement("div", {
        className: "loader-screen__content"
    }, /*#__PURE__*/ React.createElement(Loader, null), /*#__PURE__*/ React.createElement("p", {
        className: "loader-screen__text"
    }, intl.getLabel('common.loadingText')))) : null;
};

const hashPosition = ({ position  })=>`${position.lat},${position.lng}`
;
const isEqualPositionsAndOrder = (a, b)=>{
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    const hashes = a.map(hashPosition);
    for (const [index, item] of b.entries()){
        const hash = hashPosition(item);
        const hashIndex = hashes.indexOf(hash);
        if (hashIndex === -1) {
            return false;
        }
        if (hashes[index] !== hash) {
            return false;
        }
    }
    return true;
};

const loadModule = function() {
    var _ref = swcHelpers.asyncToGenerator(function*() {
        const { GoogleMap , InfoWindow , MarkerClusterer , LoadScript , Marker  } = yield import(/* webpackChunkName: "react-google-maps" */ '@react-google-maps/api');
        return {
            GoogleMap,
            InfoWindow,
            MarkerClusterer,
            LoadScript,
            Marker
        };
    });
    return function loadModule() {
        return _ref.apply(this, arguments);
    };
}();
const useGoogleMapsModule = ()=>useDynamicModule(loadModule, '@react-google-maps/api', [])
;

const formatMarkerProps = ({ url , anchor , size , scaledSize  })=>swcHelpers.objectSpread({
        url
    }, anchor ? {
        anchor: new window.google.maps.Point(anchor.x, anchor.y)
    } : {}, size ? {
        size: new window.google.maps.Size(size.width, size.height)
    } : {}, scaledSize ? {
        scaledSize: new window.google.maps.Size(scaledSize.width, scaledSize.height)
    } : {})
;
function getMarkerProps({ highlight =false , hovered =false , inactive =false , isUserMarker =false , icon: iconProp , infoWindow  }) {
    if (isUserMarker) {
        // For the user location marker, we want the default GM marker to make it stand out
        return null;
    }
    const icon = typeof iconProp === 'function' ? formatMarkerProps(iconProp({
        highlight,
        hovered,
        inactive
    })) : iconProp;
    return {
        icon,
        children: infoWindow
    };
}

const MAX_MARKERS_AMOUNT = 50;
const DEFAULT_MAX_ZOOM = 16;
const DEFAULT_SHARE_CURRENT_LOCATION_LABEL = 'Share current location';
const DEFAULT_ZOOM_IN_LABEL = 'Zoom in';
const DEFAULT_ZOOM_OUT_LABEL = 'Zoom out';
const GoogleMaps = ({ showUseMyLocation =false , onClickUseMyLocation , userLocation: userLocation1 , userLocationZoomLevel , centerOnUserLocation =false , highlight , hovered , markers , onMarkerClick , onMarkerHover , onMapClick , mapOptions , showLoader =true , center: centerProp , markersUpdateConfig , labels , id ,  })=>{
    const { googleMapsSettings: { apiKey , defaultZoom , hasCustomZoomLevel , maxZoom , defaultCenter , pin , markerClusterOptions ,  } ,  } = useGoogleMapsContext();
    const mapRef = React.useRef();
    const [markerRef1, setMarkerRef] = React.useState({});
    const [center, setCenter] = React.useState(centerProp !== null && centerProp !== void 0 ? centerProp : defaultCenter);
    const [zoom1, setZoom] = React.useState(defaultZoom);
    const [userSharingLocation, setUserSharingLocation] = React.useState(false);
    const useMyLocationControlDiv = React.useRef();
    const zoomControlDiv = React.useRef();
    const prevMarkers = usePrevious(markers);
    const [ReactGoogleMaps] = useGoogleMapsModule();
    const options = swcHelpers.objectSpread({
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        maxZoom: maxZoom !== null && maxZoom !== void 0 ? maxZoom : DEFAULT_MAX_ZOOM
    }, mapOptions, {
        zoomControl: false
    });
    // Markers that are included for actions like automatic centering, zooming
    const markersWithMapBehavior = React.useMemo(()=>{
        return markers.filter((marker)=>{
            return !!!(marker === null || marker === void 0 ? void 0 : marker.excludedFromMapBehavior);
        });
    }, [
        markers
    ]);
    const onMarkerLoad = (marker, code)=>code && setMarkerRef((prevState)=>swcHelpers.objectSpread({}, prevState, {
                [code]: marker
            })
        )
    ;
    var ref4;
    const handleZoomChanged = React.useCallback(()=>{
        var ref1;
        return mapRef.current && setZoom((ref4 = mapRef === null || mapRef === void 0 ? void 0 : (ref1 = mapRef.current) === null || ref1 === void 0 ? void 0 : ref1.getZoom()) !== null && ref4 !== void 0 ? ref4 : defaultZoom);
    }, [
        defaultZoom
    ]);
    const onHover = React.useCallback((code, markerRef)=>onMarkerHover && onMarkerHover(code, markerRef[code])
    , [
        onMarkerHover
    ]);
    const handleMapClick = React.useCallback((e)=>onMapClick && onMapClick(e)
    , [
        onMapClick
    ]);
    const handleZoomInClick = React.useCallback(()=>{
        var ref, ref2;
        var ref3;
        const currentZoom = (ref3 = mapRef === null || mapRef === void 0 ? void 0 : (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.getZoom()) !== null && ref3 !== void 0 ? ref3 : 0;
        (ref2 = mapRef.current) === null || ref2 === void 0 ? void 0 : ref2.setZoom(currentZoom + 1);
    }, []);
    const handleZoomOutClick = React.useCallback(()=>{
        var ref, ref5;
        var ref6;
        const currentZoom = (ref6 = mapRef === null || mapRef === void 0 ? void 0 : (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.getZoom()) !== null && ref6 !== void 0 ? ref6 : 0;
        (ref5 = mapRef.current) === null || ref5 === void 0 ? void 0 : ref5.setZoom(currentZoom - 1);
    }, []);
    const handleUseMyLocationClick = React.useCallback(()=>{
        onClickUseMyLocation && onClickUseMyLocation();
        userLocation1 && mapRef.current && handleCurrentPositionClick(userLocation1);
    }, [
        onClickUseMyLocation,
        userLocation1
    ]);
    const handleCurrentPositionClick = (userLocation)=>{
        if (!userLocation || !mapRef.current) {
            return;
        }
        mapRef.current.panTo(userLocation);
        setZoom(14);
    };
    const handleMarkerClick = React.useCallback((code)=>{
        if (!code) {
            return;
        }
        const highlightPos = markers.find((marker)=>marker.code === code
        );
        if (mapRef.current && highlightPos) {
            mapRef.current.panTo(highlightPos.position);
        }
        onMarkerClick && onMarkerClick(code);
    }, [
        markers,
        onMarkerClick
    ]);
    const renderMarkers = (Marker, clusterer)=>markers.filter((marker)=>!(marker.position.lat === 0 && marker.position.lng === 0)
        ).map((marker, index)=>/*#__PURE__*/ React.createElement(Marker, swcHelpers.extends({
                title: marker.title,
                key: `${marker.code}-${index}`,
                position: marker.position,
                onClick: ()=>handleMarkerClick(marker.code)
                ,
                clusterer: clusterer,
                onLoad: (mapsMarker)=>onMarkerLoad(mapsMarker, marker.code)
                ,
                onMouseOver: ()=>!marker.inactive && onHover(marker.code, markerRef1)
                ,
                onMouseOut: ()=>onHover(null, markerRef1)
            }, getMarkerProps({
                highlight: marker.code === highlight,
                hovered: !marker.inactive && marker.code === hovered,
                inactive: marker.inactive,
                isUserMarker: marker.isUserLocation,
                icon: marker.pin || pin,
                infoWindow: marker.infoWindow
            })))
        )
    ;
    const fitMarkers = React.useCallback(()=>{
        // When there are no markers or too many markers or there is a
        // zoom level defined in the query parameters, zoom out to the default zoom
        if (!markersWithMapBehavior.length || markersWithMapBehavior.length > MAX_MARKERS_AMOUNT || hasCustomZoomLevel) {
            setZoom(defaultZoom);
            setCenter(center !== null && center !== void 0 ? center : defaultCenter);
        } else if (mapRef.current) {
            var ref;
            const bounds = markersWithMapBehavior.reduce((newBounds, marker)=>newBounds.extend(marker.position)
            , new google.maps.LatLngBounds());
            (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.fitBounds(bounds);
        }
    }, [
        center,
        defaultCenter,
        defaultZoom,
        markersWithMapBehavior,
        hasCustomZoomLevel
    ]);
    const setupControls = React.useCallback(()=>{
        if ((mapOptions === null || mapOptions === void 0 ? void 0 : mapOptions.zoomControl) !== false && zoomControlDiv.current) {
            var ref;
            (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControlDiv.current);
        }
        if (showUseMyLocation && onClickUseMyLocation && useMyLocationControlDiv.current) {
            var ref7;
            (ref7 = mapRef.current) === null || ref7 === void 0 ? void 0 : ref7.controls[google.maps.ControlPosition.TOP_RIGHT].push(useMyLocationControlDiv.current);
        }
    }, [
        showUseMyLocation,
        onClickUseMyLocation,
        useMyLocationControlDiv,
        mapOptions === null || mapOptions === void 0 ? void 0 : mapOptions.zoomControl,
        zoomControlDiv, 
    ]);
    const onLoad = React.useCallback((map)=>{
        mapRef.current = map;
        setupControls();
        fitMarkers();
    }, [
        setupControls,
        fitMarkers
    ]);
    React.useEffect(()=>setZoom(defaultZoom)
    , [
        defaultZoom
    ]);
    React.useEffect(()=>centerProp && setCenter(centerProp)
    , [
        centerProp
    ]);
    React.useEffect(()=>{
        const activeUserLocation = Boolean(userLocation1);
        if (activeUserLocation && userSharingLocation !== activeUserLocation) {
            setUserSharingLocation(true);
        } else if (!activeUserLocation) {
            setUserSharingLocation(false);
        }
    }, [
        userLocation1,
        userSharingLocation
    ]);
    React.useEffect(()=>{
        if (centerOnUserLocation && userLocation1 && userSharingLocation) {
            const zoom = userLocationZoomLevel !== null && userLocationZoomLevel !== void 0 ? userLocationZoomLevel : defaultZoom;
            setZoom(zoom);
            setCenter(userLocation1);
        } else if (markersWithMapBehavior.length && (!prevMarkers || !isEqualPositionsAndOrder(prevMarkers, markers))) {
            // If `markersUpdateConfig` is provided, set center and zoom according to the config.
            if (markersUpdateConfig && Object.keys(markersUpdateConfig).length > 0) {
                if (markersUpdateConfig.centerToFirst) {
                    setCenter(markersWithMapBehavior[0].position);
                }
                if (markersUpdateConfig.zoom !== undefined && !hasCustomZoomLevel) {
                    var ref;
                    (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.setZoom(markersUpdateConfig.zoom);
                    setZoom(markersUpdateConfig.zoom);
                }
            } else {
                fitMarkers();
            }
        }
    // userLocation - don't want this effect to be trigged as we don't want it rerender and reset the zoom
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        prevMarkers,
        userLocationZoomLevel,
        centerOnUserLocation,
        defaultZoom,
        markers,
        markersWithMapBehavior,
        fitMarkers,
        markersUpdateConfig,
        userSharingLocation, 
    ]);
    React.useEffect(()=>{
        useMyLocationControlDiv.current = document.createElement('div');
        zoomControlDiv.current = document.createElement('div');
    }, []);
    const [ready, setReady] = React.useState(false);
    const scriptLoaded = React.useCallback(()=>setReady(true)
    , []);
    if (!ReactGoogleMaps) {
        return showLoader ? /*#__PURE__*/ React.createElement(LoaderScreen, null) : null;
    }
    const shouldRenderUseMyLocationButton = showUseMyLocation && onClickUseMyLocation;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ReactGoogleMaps.LoadScript, {
        googleMapsApiKey: apiKey,
        onLoad: scriptLoaded,
        loadingElement: /*#__PURE__*/ React.createElement(LoaderScreen, null)
    }, /*#__PURE__*/ React.createElement(ReactGoogleMaps.GoogleMap, {
        "data-t": "google-map",
        mapContainerClassName: "google-maps__container",
        options: options,
        onLoad: onLoad,
        onClick: handleMapClick,
        onZoomChanged: handleZoomChanged,
        zoom: zoom1,
        center: center,
        id: id
    }, ready && (markerClusterOptions ? /*#__PURE__*/ React.createElement(ReactGoogleMaps.MarkerClusterer, swcHelpers.extends({}, markerClusterOptions), (clusterer)=>/*#__PURE__*/ React.createElement(React.Fragment, null, renderMarkers(ReactGoogleMaps.Marker, clusterer))
    ) : renderMarkers(ReactGoogleMaps.Marker)))), (mapOptions === null || mapOptions === void 0 ? void 0 : mapOptions.zoomControl) !== false && zoomControlDiv.current && /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ React.createElement("div", {
        className: "google-maps__zoom-control"
    }, /*#__PURE__*/ React.createElement("button", {
        className: "google-maps__zoom-in-button",
        onClick: handleZoomInClick,
        title: (labels === null || labels === void 0 ? void 0 : labels.zoomIn) || DEFAULT_ZOOM_IN_LABEL,
        "aria-label": (labels === null || labels === void 0 ? void 0 : labels.zoomIn) || DEFAULT_ZOOM_IN_LABEL
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "plus"
    })), /*#__PURE__*/ React.createElement("button", {
        className: "google-maps__zoom-out-button",
        onClick: handleZoomOutClick,
        title: (labels === null || labels === void 0 ? void 0 : labels.zoomOut) || DEFAULT_ZOOM_OUT_LABEL,
        "aria-label": (labels === null || labels === void 0 ? void 0 : labels.zoomOut) || DEFAULT_ZOOM_OUT_LABEL
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "minus"
    }))), zoomControlDiv.current), shouldRenderUseMyLocationButton && useMyLocationControlDiv.current && /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ React.createElement("button", {
        className: "google-maps__center-position-button",
        onClick: handleUseMyLocationClick,
        title: (labels === null || labels === void 0 ? void 0 : labels.shareCurrentLocation) || DEFAULT_SHARE_CURRENT_LOCATION_LABEL,
        "aria-label": (labels === null || labels === void 0 ? void 0 : labels.shareCurrentLocation) || DEFAULT_SHARE_CURRENT_LOCATION_LABEL
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "gps"
    })), useMyLocationControlDiv.current));
};

const useGoogleMapsStaticMap = ({ lat , lon , params =''  })=>{
    const { googleMapsSettings: { apiKey  } ,  } = useGoogleMapsContext();
    if (!lat && !lon) {
        return undefined;
    }
    const mapImageUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lon}&zoom=13&size=640x200&scale=2&markers=anchor:28,48|icon:https://assets.grandvision.io/global/store2.png|scale:2%7C${lat},${lon}&mapType=roadmap&key=${apiKey}${params ? `&${params}` : ''}`;
    return mapImageUrl;
};

var InfoWindow = (_param)=>{
    var { options: optionsProp  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "options"
    ]);
    const [ReactGoogleMaps] = useGoogleMapsModule();
    if (!ReactGoogleMaps) {
        return null;
    }
    const options = swcHelpers.objectSpread({}, optionsProp, {
        pixelOffset: undefined
    });
    if (optionsProp === null || optionsProp === void 0 ? void 0 : optionsProp.pixelOffset) {
        options.pixelOffset = swcHelpers.objectSpread({}, optionsProp.pixelOffset, {
            equals: optionsProp.pixelOffset.equals ? optionsProp.pixelOffset.equals : ()=>false
        });
    }
    return /*#__PURE__*/ React__default.createElement(ReactGoogleMaps.InfoWindow, swcHelpers.extends({
        options: options
    }, props));
};

const IconButton = /*#__PURE__*/ React.forwardRef((_param, ref)=>{
    var { outline , disabled , onClick , className , children  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "outline",
        "disabled",
        "onClick",
        "className",
        "children"
    ]);
    if (!children) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("button", swcHelpers.extends({}, rest, {
        className: classnames('icon-button', {
            'icon-button--outline': outline
        }, className),
        onClick: onClick,
        disabled: disabled || false,
        ref: ref,
        type: "button"
    }), children);
});
IconButton.displayName = 'IconButton';

const Delayed = ({ delay , scrollIntoView =false , className , children ,  })=>{
    const [visibility, setVisibility] = React.useState(false);
    useDebouncedEffect(()=>setVisibility(true)
    , delay);
    const { elementRef , triggerScroll  } = useScrollIntoView();
    React.useEffect(()=>{
        if (scrollIntoView && visibility) {
            triggerScroll();
        }
    }, [
        scrollIntoView,
        triggerScroll,
        visibility
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: className,
        ref: elementRef
    }, visibility && children);
};

const useResizeEventListener = (iframeId, iframeRef, setHeight, iframeHeight, initialHeight)=>{
    const iframeEventHandler = React.useCallback((e)=>{
        if (initialHeight || !Array.isArray(e.data)) {
            return;
        }
        const [eventName, eventValue, eventIframeId] = e.data;
        if (iframeId !== eventIframeId) {
            return;
        }
        if (eventName === 'setHeight' && iframeHeight !== eventValue) {
            setHeight(eventValue);
        } else if (eventName === 'scrollTop' && iframeRef.current) {
            iframeRef.current.scrollIntoView();
            window.scrollBy(0, -100);
        }
    }, [
        initialHeight,
        iframeHeight,
        iframeId,
        iframeRef
    ]);
    useEventListener('message', iframeEventHandler);
};
const IFrame = ({ source , title , height: initialHeight , scrolling , position ='center' ,  })=>{
    if (!source) {
        return null;
    }
    const [iframeHeight, setHeight] = React.useState(initialHeight);
    const iframeId = get(QueryString.parse(source), 'iframeId', '');
    const iframeRef = React.useRef(null);
    useResizeEventListener(StringGuard.guard('iframeId', iframeId), iframeRef, setHeight, iframeHeight, initialHeight);
    return /*#__PURE__*/ React.createElement("div", {
        className: "site-container"
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames('iframe', {
            [`iframe--${position}`]: !!position
        })
    }, /*#__PURE__*/ React.createElement("iframe", {
        ref: iframeRef,
        frameBorder: "0",
        src: source,
        height: iframeHeight,
        scrolling: `${scrolling ? 'yes' : 'no'}`,
        title: title
    })));
};

class Image extends React.PureComponent {
    componentDidMount() {
        const { lazyLoadingOptions  } = this.props;
        if (lazyLoadingOptions) {
            // Set the image quality back to default from a low quality image state
            this.setState({
                q: '',
                auto: ''
            });
        }
    }
    render() {
        const { url , onClick , className , children  } = this.props;
        const { q , auto  } = this.state;
        const urlOptionQuality = q && `&q=${q}`;
        const urlOptionAutomatic = auto && `&auto=${auto}`;
        const lazyLoadingUrl = url + urlOptionQuality + urlOptionAutomatic;
        return onClick ? /*#__PURE__*/ React.createElement("button", {
            className: classnames('image', className),
            onClick: onClick
        }, this.renderImage(lazyLoadingUrl, this.props)) : /*#__PURE__*/ React.createElement("div", {
            className: classnames('image', className)
        }, this.renderImage(lazyLoadingUrl, this.props), children);
    }
    constructor(...args){
        super(...args);
        this.state = {
            q: this.props.lazyLoadingOptions && this.props.lazyLoadingOptions.q || '',
            auto: this.props.lazyLoadingOptions && this.props.lazyLoadingOptions.auto || ''
        };
        this.renderImage = (lazyLoadingUrl, _param)=> {
            var { lazyLoadingOptions , cover , alt  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
                "lazyLoadingOptions",
                "cover",
                "alt"
            ]);
            return React.createElement("span", {
                className: "image__background-image",
                role: "img",
                "aria-label": Boolean(rest['aria-hidden']) ? undefined : alt,
                "aria-hidden": rest['aria-hidden'],
                style: {
                    backgroundImage: `url('${lazyLoadingUrl}')`,
                    backgroundSize: `${cover ? 'cover' : 'contain'}`
                }
            });
        };
    }
}

const LazyImage = ({ src , alt , className , width , height , rootMargin ='0px 0px' , srcSet , sizes , placeholder ,  })=>{
    if (!src || !width || !height) {
        console.warn('Width and height are required when using LazyImage');
        return null;
    }
    const [ref, inView] = useInView({
        rootMargin,
        triggerOnce: true
    });
    const renderImage = ()=>srcSet && sizes ? /*#__PURE__*/ React.createElement(ResponsiveImage, {
            alt: alt,
            src: src,
            srcSet: srcSet,
            sizes: sizes
        }) : /*#__PURE__*/ React.createElement("img", {
            src: src,
            alt: alt,
            width: width,
            height: height
        })
    ;
    return /*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: classnames('lazy-image', className),
        style: {
            paddingBottom: `${height / width * 100}%`
        }
    }, inView ? renderImage() : placeholder || null);
};

const LineItem = /*#__PURE__*/ forwardRef((_param, ref)=> {
    var { className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className"
    ]);
    return React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('elements-line-item', className),
        ref: ref
    }));
});
LineItem.displayName = 'LineItem';

const Price = (_param)=>{
    var { value , centAmount =true , currency , className , type ='default' , size ='custom' , 'aria-label': ariaLabel  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "value",
        "centAmount",
        "currency",
        "className",
        "type",
        "size",
        'aria-label'
    ]);
    const { locale  } = useIntl();
    return /*#__PURE__*/ React.createElement("span", swcHelpers.extends({}, rest, {
        className: classnames('price', `price--${type}`, `price--${size}`, className)
    }), ariaLabel && /*#__PURE__*/ React.createElement("span", {
        className: "sr-only"
    }, ariaLabel, "\xa0"), formatPrice({
        locale,
        value,
        currency,
        isCentAmount: !!centAmount
    }));
};

const priceBaseClassName = 'elements-line-item-aside__price';
const LineItemAside = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { children , className , currency , discountPrice , price  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className",
        "currency",
        "discountPrice",
        "price"
    ]);
    const { getLabel  } = useIntl();
    let content = children;
    if (discountPrice === 0) {
        content = /*#__PURE__*/ React__default.createElement("p", {
            className: `${priceBaseClassName} ${priceBaseClassName}--free`
        }, /*#__PURE__*/ React__default.createElement("span", {
            className: "price"
        }, getLabel('products.freeProduct')));
    } else if (!children && price) {
        const classNames = classnames(priceBaseClassName, {
            [`${priceBaseClassName}--strikethrough`]: typeof discountPrice === 'number'
        });
        content = /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("p", {
            className: classNames
        }, /*#__PURE__*/ React__default.createElement(Price, {
            value: price,
            currency: currency
        })), discountPrice && /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, "\xa0", /*#__PURE__*/ React__default.createElement("p", {
            className: priceBaseClassName
        }, /*#__PURE__*/ React__default.createElement(Price, {
            value: discountPrice,
            currency: currency,
            type: "discount"
        }))));
    }
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('elements-line-item-aside', className),
        ref: ref
    }), content);
});
LineItemAside.displayName = 'LineItemAside';

const PriceComponent = ({ priceIsZeroLabel , discountText , originalPrice , discountedPrice , currency ,  })=>{
    if (originalPrice === 0) {
        return /*#__PURE__*/ React.createElement(Card.Text, {
            variant: "caption"
        }, priceIsZeroLabel);
    }
    if (discountedPrice === 0 && discountText && (discountText === null || discountText === void 0 ? void 0 : discountText.length) > 0) {
        return /*#__PURE__*/ React.createElement(Card.Text, {
            variant: "caption"
        }, discountText);
    }
    const isDiscounted = isNumber(discountedPrice);
    return /*#__PURE__*/ React.createElement(Card.Text, {
        variant: "caption",
        className: "line-item-bundle-row__price"
    }, /*#__PURE__*/ React.createElement(Price, {
        value: isDiscounted ? discountedPrice : originalPrice,
        currency: currency
    }), isDiscounted ? /*#__PURE__*/ React.createElement(Price, {
        type: "strikethrough",
        currency: currency,
        value: originalPrice
    }) : null);
};
var LineItemBundleRow = (props)=>{
    const { title , body  } = props, priceInfo = swcHelpers.objectWithoutProperties(props, [
        "title",
        "body"
    ]);
    const { getLabel  } = useIntl();
    const priceIsZeroLabel = priceInfo.priceIsZeroLabel || getLabel('cart.lineItem.glasses.isIncluded', {
        hideLabelKey: true
    });
    return /*#__PURE__*/ React.createElement(Card.Section, {
        className: "line-item-bundle-row__section"
    }, /*#__PURE__*/ React.createElement(Card.Section, {
        spacing: "none"
    }, title && /*#__PURE__*/ React.createElement(Card.Text, {
        variant: "caption-head"
    }, title), /*#__PURE__*/ React.createElement(Card.Text, {
        variant: "caption"
    }, body)), /*#__PURE__*/ React.createElement(Card.Section, {
        spacing: "none"
    }, /*#__PURE__*/ React.createElement(PriceComponent, swcHelpers.extends({}, priceInfo, {
        priceIsZeroLabel: priceIsZeroLabel
    }))));
};

const LineItemContent = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { body , children , className , title  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "body",
        "children",
        "className",
        "title"
    ]);
    let content = children;
    if (!content) {
        content = /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Heading, {
            variant: "smallest",
            className: "elements-line-item-content__title"
        }, title), /*#__PURE__*/ React__default.createElement("div", {
            className: "elements-line-item-content__body"
        }, body));
    }
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('elements-line-item-content', className),
        ref: ref
    }), content);
});
LineItemContent.displayName = 'LineItemContent';

const LineItemDeleteButton = (_param)=>{
    var { className , onClick  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "onClick"
    ]);
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement(IconButton, {
        onClick: onClick,
        "data-t": rest['data-t'],
        className: classnames('elements-line-item-delete', className),
        "aria-label": getLabel('common.actions.delete')
    }, /*#__PURE__*/ React.createElement(Icon, {
        className: "elements-line-item-delete__icon",
        name: "trash"
    }));
};

const lineItemClassName$1 = 'line-item-prescription';
const allowedKeys = [
    'power',
    'baseCurve',
    'diameter',
    'cylinder',
    'axis',
    'addition',
    'pupillaryDistance', 
];
const Row$1 = ({ prescriptionAttributes , intl  })=>{
    const { getLabel  } = intl;
    const sortedParams = sortPrescriptionParameters(Object.keys(prescriptionAttributes).filter((attribute)=>allowedKeys.includes(attribute)
    ));
    return /*#__PURE__*/ React.createElement(React.Fragment, null, sortedParams.map((key)=>{
        const value = PrescriptionValueUtils.formatPrescriptionValue(key, prescriptionAttributes[key]);
        if (!isPresent(value)) return null;
        return /*#__PURE__*/ React.createElement("span", {
            className: `${lineItemClassName$1}__item`,
            key: key
        }, getLabel(`cart.lineItem.glasses.prescription.${key}`), ": ", value);
    }));
};
const NewPrescriptionDetails$1 = ({ rightTitle , leftTitle , intl , rightLens , leftLens ,  })=>/*#__PURE__*/ React.createElement("div", {
        className: `${lineItemClassName$1}__prescription-group`
    }, rightLens && /*#__PURE__*/ React.createElement("div", {
        className: `${lineItemClassName$1}__group`
    }, /*#__PURE__*/ React.createElement("span", {
        className: `${lineItemClassName$1}__item ${lineItemClassName$1}__title`
    }, rightTitle), /*#__PURE__*/ React.createElement(Row$1, {
        prescriptionAttributes: rightLens,
        intl: intl
    })), leftLens && /*#__PURE__*/ React.createElement("div", {
        className: `${lineItemClassName$1}__group`
    }, /*#__PURE__*/ React.createElement("span", {
        className: `${lineItemClassName$1}__item ${lineItemClassName$1}__title`
    }, leftTitle), /*#__PURE__*/ React.createElement(Row$1, {
        prescriptionAttributes: leftLens,
        intl: intl
    })))
;
const PrescriptionMessage$1 = ({ title , message  })=>/*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("span", {
        className: `${lineItemClassName$1}__title`
    }, title), message)
;
const Body = ({ prescription , intl  })=>{
    const { getLabel  } = intl;
    switch(prescription.type){
        case PrescriptionType.PRESCRIPTION_FILE:
            return /*#__PURE__*/ React.createElement(FilesList, {
                items: [
                    prescription.pupillaryDistanceFile,
                    prescription.prescriptionFile
                ]
            });
        case PrescriptionType.NEW_PRESCRIPTION:
            const { rightLens , leftLens  } = prescription;
            return /*#__PURE__*/ React.createElement(NewPrescriptionDetails$1, {
                rightTitle: `${getLabel('common.right')}: `,
                leftTitle: `${getLabel('common.left')}: `,
                rightLens: rightLens,
                leftLens: leftLens,
                intl: intl
            });
        case PrescriptionType.EMAIL_PRESCRIPTION:
            return /*#__PURE__*/ React.createElement(PrescriptionMessage$1, {
                title: `${getLabel('cart.lineItem.glasses.emailPrescription.title')}: `,
                message: `${getLabel('cart.lineItem.glasses.emailPrescription.message')}`
            });
        case PrescriptionType.EXISTING_CUSTOMER:
            return /*#__PURE__*/ React.createElement(PrescriptionMessage$1, {
                title: `${getLabel('cart.lineItem.glasses.noPrescription.title')}: `,
                message: `${getLabel('cart.lineItem.glasses.noPrescription.message')}`
            });
    }
};
const LineItemPrescription = (_param)=>{
    var { prescription , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "prescription",
        "className"
    ]);
    const intl = useIntl();
    const { getLabel  } = intl;
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames([
            lineItemClassName$1,
            className
        ]),
        "data-t": rest['data-t']
    }, /*#__PURE__*/ React.createElement("p", {
        className: `${lineItemClassName$1}__headline`
    }, getLabel('account.order.labels.prescriptionDetails')), /*#__PURE__*/ React.createElement(Body, {
        prescription: prescription,
        intl: intl
    }));
};

const lineItemClassName = 'line-item-glasses-prescription';
const Row = ({ prescriptionAttributes  })=>{
    const { getLabel  } = useIntl();
    const sortedParams = sortPrescriptionParameters(Object.keys(prescriptionAttributes));
    return /*#__PURE__*/ React.createElement(React.Fragment, null, sortedParams.map((key)=>/*#__PURE__*/ React.createElement("span", {
            className: `${lineItemClassName}__item`,
            key: key
        }, getLabel(`cart.lineItem.glasses.prescription.${key}`), ":", ' ', PrescriptionValueUtils.formatPrescriptionValue(key, prescriptionAttributes[key]))
    ));
};
const Wrapper = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React.createElement("div", {
        className: classnames(lineItemClassName, className),
        "data-t": rest['data-t']
    }, children);
};
const NewPrescriptionDetails = ({ rightLens , leftLens  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: `${lineItemClassName}__group`
    }, /*#__PURE__*/ React.createElement("span", {
        className: `${lineItemClassName}__item ${lineItemClassName}__title`
    }, getLabel('common.right'), ":", ' '), /*#__PURE__*/ React.createElement(Row, {
        prescriptionAttributes: rightLens
    })), /*#__PURE__*/ React.createElement("div", {
        className: `${lineItemClassName}__group`
    }, /*#__PURE__*/ React.createElement("span", {
        className: `${lineItemClassName}__item ${lineItemClassName}__title`
    }, getLabel('common.left'), ":", ' '), /*#__PURE__*/ React.createElement(Row, {
        prescriptionAttributes: leftLens
    })));
};
const PrescriptionFileDetails = ({ pupillaryDistanceFileName , prescriptionFileName ,  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: `${lineItemClassName}__title`
    }, getLabel(`cart.lineItem.glasses.prescriptionFile.title`), ":", ' '), /*#__PURE__*/ React.createElement(FilesList, {
        className: `${lineItemClassName}__files-list`,
        items: [
            pupillaryDistanceFileName,
            prescriptionFileName
        ]
    }));
};
const PrescriptionMessage = ({ prescription  })=>{
    const { getLabel  } = useIntl();
    const labelKey = (prescription === null || prescription === void 0 ? void 0 : prescription.type) === 'ExistingCustomer' ? 'noPrescription' : 'emailPrescription';
    return /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("span", {
        className: `${lineItemClassName}__title`
    }, getLabel(`cart.lineItem.glasses.${labelKey}.title`), ":", ' '), getLabel(`cart.lineItem.glasses.${labelKey}.message`));
};
const LineItemGlassesPrescription = (_param)=>{
    var { prescription , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "prescription",
        "className"
    ]);
    const wrapperProps = {
        className,
        'data-t': rest['data-t']
    };
    if ((prescription === null || prescription === void 0 ? void 0 : prescription.type) === 'PrescriptionFile') {
        const { prescriptionFile , pupillaryDistanceFile  } = prescription;
        return /*#__PURE__*/ React.createElement(Wrapper, {
            className: `${lineItemClassName}--column`
        }, /*#__PURE__*/ React.createElement(PrescriptionFileDetails, {
            pupillaryDistanceFileName: pupillaryDistanceFile,
            prescriptionFileName: prescriptionFile
        }));
    }
    if ((prescription === null || prescription === void 0 ? void 0 : prescription.type) === 'NewPrescription') {
        const { rightLens , leftLens  } = prescription;
        return /*#__PURE__*/ React.createElement(Wrapper, swcHelpers.extends({}, wrapperProps), /*#__PURE__*/ React.createElement(NewPrescriptionDetails, {
            rightLens: rightLens,
            leftLens: leftLens
        }));
    }
    if ((prescription === null || prescription === void 0 ? void 0 : prescription.type) === 'ExistingCustomer' || (prescription === null || prescription === void 0 ? void 0 : prescription.type) === 'EmailPrescription') {
        return /*#__PURE__*/ React.createElement(Wrapper, swcHelpers.extends({}, wrapperProps), /*#__PURE__*/ React.createElement(PrescriptionMessage, {
            prescription: prescription
        }));
    }
    return null;
};

const addLinkToComponent = (component, href)=>{
    if (href) {
        return /*#__PURE__*/ React__default.createElement("a", {
            className: "elements-line-item-media__link",
            href: href
        }, component);
    }
    return component;
};
const LineItemMedia = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { href , children , className , imageUrl , imageAlt , iconName , iconLabel , type , overlayElement , width  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "href",
        "children",
        "className",
        "imageUrl",
        "imageAlt",
        "iconName",
        "iconLabel",
        "type",
        "overlayElement",
        "width"
    ]);
    const containerClasses = classnames('elements-line-item-media__container', {
        'elements-line-item-media__container--with-overlay': !!overlayElement
    });
    let content = children;
    if (!content) {
        if (type === 'image') {
            content = /*#__PURE__*/ React__default.createElement("div", {
                className: containerClasses
            }, /*#__PURE__*/ React__default.createElement("img", {
                className: classnames('elements-line-item-media__image', {
                    [`elements-line-item-media__image--${width}`]: width,
                    [`${className}__image`]: className
                }),
                src: imageUrl,
                alt: imageAlt
            }));
        }
        if (type === 'icon' && iconName) {
            content = /*#__PURE__*/ React__default.createElement("div", {
                className: containerClasses
            }, /*#__PURE__*/ React__default.createElement("div", {
                className: "elements-line-item-media__icon"
            }, /*#__PURE__*/ React__default.createElement(Icon, {
                name: iconName,
                width: 48,
                height: 48
            }), !!iconLabel && /*#__PURE__*/ React__default.createElement("span", {
                className: "elements-line-item-media__icon-label"
            }, iconLabel)));
        }
    }
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('elements-line-item-media', className),
        ref: ref
    }), addLinkToComponent(content, href));
});
LineItemMedia.displayName = 'LineItemMedia';

const LineItemRowItem = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('line-item-row__item', className)
    }), children);
};
var LineItemRow = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React__default.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('line-item-row', className)
    }), children);
};
LineItemRow.Item = LineItemRowItem;

const LineItemSubtitle = ({ className , subtitle  })=>/*#__PURE__*/ React.createElement("p", {
        className: classnames('elements-line-item-subtitle', className)
    }, subtitle)
;

const LineItemTitle = ({ className , title  })=>/*#__PURE__*/ React.createElement(Heading, {
        component: "h2",
        variant: "subheading1",
        className: classnames('elements-line-item-title', className)
    }, title)
;

const List = ({ className , icon ='default' , spacing ='default' , items , numerical =false , renderText ,  })=>{
    if (items.length < 1) {
        return null;
    }
    const hasIcons = icon !== 'default';
    const hasSmallIcons = icon === null || icon === void 0 ? void 0 : icon.includes('-small');
    const iconSize = hasSmallIcons ? 18 : 24;
    return /*#__PURE__*/ React.createElement("ul", {
        "data-t": "list",
        className: classnames('list', className, {
            'list--with-icons': hasIcons,
            'list--with-small-icons': hasSmallIcons
        })
    }, items.map((item, index)=>/*#__PURE__*/ React.createElement("li", {
            key: item,
            className: classnames('list__item', {
                'list__item--numerical': numerical,
                [`list__item--${icon}`]: !numerical,
                'list__item--compact': spacing === 'compact'
            })
        }, icon !== 'default' && !numerical && /*#__PURE__*/ React.createElement(Icon, {
            name: "checkmark-circle",
            className: "list__item-icon",
            width: iconSize,
            height: iconSize
        }), numerical && /*#__PURE__*/ React.createElement("div", {
            className: "list__item-number"
        }, index + 1), renderText ? renderText(item) : /*#__PURE__*/ React.createElement("span", {
            className: "list__item-text"
        }, item))
    ));
};
List.displayName = 'List';

class LockBodyScroll extends Component {
    componentDidMount() {
        lockScroll();
    }
    componentWillUnmount() {
        unlockScroll();
    }
    render() {
        return null;
    }
}

const Logo = ({ url , className , image , title , srOnlyText  })=>/*#__PURE__*/ React.createElement("a", {
        className: `logo ${className || ''}`,
        href: url,
        title: title,
        "data-t": "logo-link"
    }, /*#__PURE__*/ React.createElement("img", {
        className: "logo__img",
        src: image.url,
        alt: title,
        "data-t": "logo-image"
    }), srOnlyText && /*#__PURE__*/ React.createElement("span", {
        className: "sr-only",
        "data-t": "logo-sr-only"
    }, srOnlyText))
;

const getIconAsSvgString = (width, height, color, textColor)=>`<svg width="${width}" height="${height}" viewBox="0 0 36 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#shadow)">
        <circle cx="18" cy="16" r="13" fill="${color}" stroke="${textColor}" stroke-width="2"/>
        </g>
        <defs>
        <filter id="shadow" x="0" y="0" width="36" height="34" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="2"/>
        <feGaussianBlur stdDeviation="2"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_23086_30000"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset/>
        <feGaussianBlur stdDeviation="1"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
        <feBlend mode="normal" in2="effect1_dropShadow_23086_30000" result="effect2_dropShadow_23086_30000"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_23086_30000" result="shape"/>
        </filter>
        </defs>
      </svg>
`
;

const clusterSizes = [
    {
        width: 24,
        height: 25
    },
    {
        width: 41,
        height: 42
    },
    {
        width: 56,
        height: 58
    },
    {
        width: 72,
        height: 74
    },
    {
        width: 88,
        height: 90
    }, 
];
const markerClusterStyles = ({ theme , size  })=>{
    const textSize = 12;
    const height = size.height;
    const width = size.width;
    const color = theme.color;
    const textColor = theme.textColor;
    const svgString = getIconAsSvgString(width, height, color, textColor);
    return {
        url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`,
        width,
        height,
        textColor,
        textSize
    };
};
const MarkerClusters = (props)=>{
    const { theme , maxZoom =10 , minimumClusterSize =2  } = props;
    const styles = clusterSizes.map((clusterSize)=>markerClusterStyles({
            size: clusterSize,
            theme
        })
    );
    return {
        styles,
        maxZoom,
        minimumClusterSize
    };
};

const Modal = (_param)=>{
    var { activeTrap , 'aria-label': ariaLabel , isOpen , isFullScreen =false , preventVerticalScroll , className , title , footer , showIcon , secondaryIconOptions , iconClassName , onModalClose , children , isOutsideClickEnabled =true  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "activeTrap",
        'aria-label',
        "isOpen",
        "isFullScreen",
        "preventVerticalScroll",
        "className",
        "title",
        "footer",
        "showIcon",
        "secondaryIconOptions",
        "iconClassName",
        "onModalClose",
        "children",
        "isOutsideClickEnabled"
    ]);
    const [modalRoot, setModalRoot] = React.useState(null);
    const { getLabel  } = useIntl();
    // In many cases, the Modal is not conditionally rendered, which means setting the isOpen to false,
    // will not trigger the unlockScroll returned from useEffect.
    // We have to keep track of isOpen prop so that if it changed from true to false, we have to unlockScroll
    const isOpenRef = React.useRef(isOpen);
    // The modal should only be closed by calling this method!
    // Using effects for closing the modal would cause some unpredictable situations e.g. double scrollbar
    const hideModal = React.useCallback(()=>{
        unlockScroll();
        onModalClose === null || onModalClose === void 0 ? void 0 : onModalClose();
    }, [
        onModalClose
    ]);
    const hideOnEscKey = React.useCallback((event)=>{
        if (event.key === 'Escape' || event.key === 'Esc') {
            hideModal();
        }
    }, [
        hideModal
    ]);
    React.useEffect(()=>{
        document.addEventListener('keydown', hideOnEscKey, false);
        return ()=>{
            document.removeEventListener('keydown', hideOnEscKey, false);
        };
    }, [
        hideOnEscKey
    ]);
    React.useEffect(()=>{
        isOpenRef.current = isOpenRef.current || isOpen;
        // a Modal may only lock/unlock if it's open
        if (isOpen) {
            lockScroll();
            return unlockScroll;
        } else if (isOpenRef.current) {
            // removing the scroll lock, on Modal close but not unmounted
            unlockScroll();
        }
    }, [
        isOpen
    ]);
    React.useEffect(()=>{
        setModalRoot(document ? document.body : null);
    }, []);
    const transition = {
        stiffness: 500
    };
    const variants = {
        exit: {
            opacity: 0,
            y: 50
        },
        idle: {
            opacity: 1,
            y: 0
        }
    };
    if (!modalRoot) {
        return null;
    }
    return /*#__PURE__*/ ReactDOM__default.createPortal(/*#__PURE__*/ React.createElement(AnimatePresence, null, isOpen && /*#__PURE__*/ React.createElement(FocusTrap, {
        active: activeTrap,
        focusTrapOptions: {
            clickOutsideDeactivates: true
        }
    }, /*#__PURE__*/ React.createElement("div", {
        "data-t": rest['data-t'],
        className: classnames('modal', className)
    }, /*#__PURE__*/ React.createElement(Backdrop, {
        className: "modal__backdrop",
        onClick: isOutsideClickEnabled ? hideModal : undefined,
        isVisible: isOpen,
        animate: false
    }), /*#__PURE__*/ React.createElement(motion.div, {
        transition: transition,
        variants: variants,
        initial: "exit",
        animate: "idle",
        exit: "exit",
        className: classnames('modal__body', {
            'modal__body--flex': !!title || !!footer,
            'modal__body--fullscreen': isFullScreen,
            'modal__body--vertical-scroll-lock': preventVerticalScroll,
            [`${className}__body`]: !!className
        }),
        "data-t": "modal-body",
        "aria-label": ariaLabel || title,
        role: "dialog",
        "aria-modal": "true"
    }, secondaryIconOptions && /*#__PURE__*/ React.createElement(IconButton, {
        "aria-label": secondaryIconOptions.label || secondaryIconOptions['aria-label'],
        className: classnames('modal__secondary-icon', {
            'modal__secondary-icon--with-label': !!secondaryIconOptions.label
        }),
        onClick: secondaryIconOptions.onClick
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: secondaryIconOptions.name
    }), !!secondaryIconOptions.label && /*#__PURE__*/ React.createElement("span", {
        className: "modal__secondary-icon-label"
    }, secondaryIconOptions.label)), showIcon && /*#__PURE__*/ React.createElement(IconButton, {
        className: classnames('modal__close', iconClassName),
        "aria-label": getLabel('common.actions.close'),
        onClick: hideModal
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "close"
    })), title && /*#__PURE__*/ React.createElement("header", {
        className: classnames('modal__header', {
            [`${className}__header`]: !!className
        })
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "modal__title"
    }, title), /*#__PURE__*/ React.createElement(Hr, null)), /*#__PURE__*/ React.createElement("div", {
        className: classnames('modal__content', {
            [`${className}__content`]: !!className,
            'modal__content--with-footer': !!footer
        })
    }, children), footer && /*#__PURE__*/ React.createElement("footer", {
        className: classnames('modal__footer', {
            [`${className}__footer`]: !!className
        })
    }, /*#__PURE__*/ React.createElement(Hr, null), footer))))), modalRoot);
};

const ModalFooter = ({ ctaCaption , onCancel , onCta , ctaButtonType , cancelCaption , associatedFormId , ctaLoading =false , className ,  })=>{
    const isPhone = useIsPhone();
    const { getLabel  } = useIntl();
    const cancelProps = useMemo(()=>({
            'data-t': 'modal-footer-cancel-button',
            className: 'modal-footer__button',
            caption: cancelCaption !== null && cancelCaption !== void 0 ? cancelCaption : getLabel('common.actions.cancel'),
            'aria-label': cancelCaption !== null && cancelCaption !== void 0 ? cancelCaption : getLabel('common.actions.cancel'),
            variant: 'outline',
            onClick: onCancel
        })
    , [
        cancelCaption,
        getLabel,
        onCancel
    ]);
    const ctaProps = useMemo(()=>({
            'data-t': 'modal-footer-cta-button',
            className: 'modal-footer__button',
            caption: ctaCaption,
            'aria-label': ctaCaption,
            variant: 'solid',
            onClick: onCta,
            type: ctaButtonType,
            form: associatedFormId,
            isLoading: ctaLoading
        })
    , [
        associatedFormId,
        ctaButtonType,
        ctaCaption,
        ctaLoading,
        onCta
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('modal-footer', className)
    }, isPhone ? /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Button, swcHelpers.extends({}, ctaProps)), /*#__PURE__*/ React__default.createElement(Button, swcHelpers.extends({}, cancelProps))) : /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Button, swcHelpers.extends({}, cancelProps)), /*#__PURE__*/ React__default.createElement(Button, swcHelpers.extends({}, ctaProps))));
};

const NotificationBar = ({ title , description , theme ='dark' , link ={}  })=>{
    const { getLabel  } = useIntl();
    const [isHidden, setIsHidden] = React.useState(false);
    const handleClose = React.useCallback(()=>setIsHidden(true)
    , []);
    if (isHidden) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: `notification-bar notification-bar--${theme}`
    }, /*#__PURE__*/ React.createElement("div", {
        className: "notification-bar__container"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "subheading1",
        className: "notification-bar__title"
    }, title), description && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("span", {
        className: "notification-bar__separator"
    }, "-"), /*#__PURE__*/ React.createElement("p", {
        className: "notification-bar__description"
    }, description)), link.url && link.label && /*#__PURE__*/ React.createElement(Link, {
        className: "notification-bar__link",
        url: link.url,
        label: link.label,
        target: link.target
    }), /*#__PURE__*/ React.createElement("button", {
        className: "notification-bar__close-icon",
        "aria-label": getLabel('common.actions.close'),
        onClick: handleClose
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "close"
    }))));
};

const OlapicContext = /*#__PURE__*/ React__default.createContext('');
const OlapicProvider = ({ sku , children  })=>/*#__PURE__*/ React__default.createElement(OlapicContext.Provider, {
        value: sku
    }, children)
;
const useOlapicContext = ()=>React__default.useContext(OlapicContext)
;

const Olapic = ({ apiKey , instance , locale , olapic , src , tags  })=>{
    const sku = useOlapicContext();
    const consentResult = useCookieConsentContext();
    const COOKIE_CONSENT = 'social';
    React__default.useEffect(()=>{
        if (isCookieCategoryEnabled(COOKIE_CONSENT, consentResult)) {
            //Create a script tag with the Olapic API
            const newScript = document.createElement('script');
            newScript.id = 'olapic-script';
            newScript.src = src;
            newScript.defer = true;
            newScript.type = 'text/javascript';
            newScript.setAttribute('data-instance', instance);
            newScript.setAttribute('data-tags', sku || tags);
            newScript.setAttribute('data-olapic', olapic);
            newScript.setAttribute('data-lang', locale);
            newScript.setAttribute('data-use-opi', 'false');
            newScript.setAttribute('data-apikey', apiKey);
            //Insert the script tag into the parent div of olapic
            const olapicDiv = document.getElementById(olapic);
            if (olapicDiv && olapicDiv.parentNode) {
                olapicDiv.parentNode.appendChild(newScript);
            }
        }
        return ()=>{
            const olapicScript = document.getElementById('olapic-script');
            if (olapicScript) {
                olapicScript.remove();
            }
        };
    }, [
        apiKey,
        consentResult,
        instance,
        locale,
        olapic,
        sku,
        src,
        tags
    ]);
    // Not allowed to render this component without the cookie consent
    if (!isCookieCategoryEnabled(COOKIE_CONSENT, consentResult)) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        id: olapic
    }));
};

const PriceTag = ({ originalPrice , originalCentAmount =true , discountedPrice , discountedCentAmount =true , currency ='EUR' , className ,  })=>{
    const { getLabel  } = useIntl();
    const showDiscount = typeof discountedPrice !== 'undefined';
    return /*#__PURE__*/ React.createElement("span", {
        className: classnames('price-tag', className)
    }, discountedPrice !== originalPrice && /*#__PURE__*/ React.createElement(Price, {
        "aria-label": showDiscount ? getLabel('common.originalPrice') : undefined,
        value: originalPrice,
        centAmount: originalCentAmount,
        currency: currency,
        type: showDiscount ? 'strikethrough' : 'default',
        className: "price-tag__price"
    }), showDiscount && /*#__PURE__*/ React.createElement(Price, {
        "aria-label": getLabel('common.discountedPrice'),
        value: discountedPrice,
        centAmount: discountedCentAmount,
        currency: currency,
        type: "discount",
        className: "price-tag__price"
    }));
};

const icons = {
    astigmatism: 'astigmatism',
    bestSeller: 'shopping-basket-star',
    blueLightFilter: 'blue-light-filter',
    sustainable: 'sustainable',
    forBaby: 'for-baby',
    forDryEyes: 'for-dry-eyes',
    forKids: 'for-kids',
    includingGlasses: 'frame',
    lastChance: 'last-chance',
    mirroredGlasses: 'glasses-futuristic',
    multifocal: 'store-eye-test',
    newCollection: 'new-collection',
    onlineOnly: 'happy-computer',
    polarisedGlasses: 'polarised-glasses',
    prescription: 'store-eye-test',
    seenOnTv: 'seen-on-tv',
    staffPick: 'shop-assistant',
    storeOnly: 'store',
    threeDPrinted: 'three-d-printed',
    trendy: 'trendy',
    twoMonthsForFree: 'shipment-next',
    uvProtection: 'polarised-glasses',
    smartGlasses: 'smart-glasses'
};
const ProductTag = ({ className , tag  })=>{
    if (!tag || !tag.value) return null;
    const iconName = tag.key ? icons[tag.key] : null;
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('product-block-tag', {
            'product-block-tag--with-icon': !!iconName
        }, className)
    }, iconName && /*#__PURE__*/ React__default.createElement("div", {
        className: "product-block-tag__icon"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: iconName,
        height: 14,
        width: 14
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "product-block-tag__text"
    }, tag.value));
};

const renderProgressBar = (max, value)=>/*#__PURE__*/ React.createElement("progress", {
        className: "progress-bar__value",
        max: max,
        value: value
    }, value && `${value}%`)
;
const ProgressBar = ({ label , max , value , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('progress-bar', className)
    }, label ? /*#__PURE__*/ React.createElement("label", {
        className: "progress-bar__label"
    }, label, renderProgressBar(max, value)) : renderProgressBar(max, value))
;

const QuantitySelector = (props)=>{
    const { quantity , onDecreaseQuantity , onIncreaseQuantity , minimumQuantity =1 , maximumQuantity , isDisabled =false ,  } = props;
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("div", {
        className: "quantity-selector",
        "data-t": props['data-t']
    }, /*#__PURE__*/ React.createElement(IconButton, {
        "aria-label": getLabel('cart.item.quantity.decrement'),
        className: "quantity-selector__button quantity-selector__button--minus",
        disabled: quantity === minimumQuantity || isDisabled,
        onClick: onDecreaseQuantity
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "minus"
    })), /*#__PURE__*/ React.createElement("div", {
        className: "quantity-selector__amount"
    }, quantity), /*#__PURE__*/ React.createElement(IconButton, {
        "aria-label": getLabel('cart.item.quantity.increment'),
        className: "quantity-selector__button quantity-selector__button--plus",
        disabled: maximumQuantity && quantity >= maximumQuantity || isDisabled,
        onClick: onIncreaseQuantity
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "plus"
    })));
};

const RadioButton = (_param)=> {
    var { name , variant ='outline' , children , className , disabled =false , isChecked , onChange , position ='left' , value , id , 'data-t': dataT  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "name",
        "variant",
        "children",
        "className",
        "disabled",
        "isChecked",
        "onChange",
        "position",
        "value",
        "id",
        'data-t'
    ]);
    return React.createElement("label", swcHelpers.extends({
        htmlFor: id || value,
        className: classnames('radio-button', `radio-button--${variant}`, `radio-button--${position}`, className),
        "data-t": dataT
    }, rest), /*#__PURE__*/ React.createElement("input", {
        type: "radio",
        name: name || 'radio-button',
        id: id || value,
        "aria-checked": isChecked,
        checked: isChecked,
        disabled: disabled,
        onChange: onChange,
        value: value,
        className: "radio-button__input"
    }), variant === 'outline' ? /*#__PURE__*/ React.createElement(Card.OldCard, {
        className: "radio-button__label"
    }, children) : /*#__PURE__*/ React.createElement("div", {
        className: "radio-button__label"
    }, children), /*#__PURE__*/ React.createElement("div", {
        className: "radio-button__radio-indicator"
    }));
};

const variants$1 = {
    enter: ({ index  })=>({
            y: 0,
            opacity: 1,
            transition: {
                delay: index * 0.05,
                duration: 0.2,
                ease: 'easeIn'
            }
        })
    ,
    exit: {
        y: 10,
        opacity: 0
    }
};
var RadioButtonGroup = (_param)=>{
    var { initialValue , onChange , value  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "initialValue",
        "onChange",
        "value"
    ]);
    const initialSelectedValue = value ? value : initialValue;
    const [selectedValue, setSelectedValue] = React.useState(initialSelectedValue || '');
    const handleChange = React.useCallback((event)=>{
        const newSelectedValue = event.currentTarget.value;
        setSelectedValue(newSelectedValue);
        onChange(newSelectedValue);
    }, [
        onChange
    ]);
    React.useEffect(()=>{
        if (!isNil(value)) {
            setSelectedValue(value);
        }
    }, [
        value
    ]);
    const { items , scope , className , hasError  } = props;
    const classNames = classnames('radio-button-group', className);
    return /*#__PURE__*/ React.createElement("ul", {
        className: classNames,
        "data-t": props['data-t'],
        role: "radiogroup"
    }, /*#__PURE__*/ React.createElement(AnimatePresence, null, items.length > 0 && items.map((item, index)=>/*#__PURE__*/ React.createElement(motion.li, {
            className: "radio-button-group__item",
            key: item.value,
            custom: {
                index
            },
            variants: variants$1,
            initial: "exit",
            animate: "enter",
            role: "presentation"
        }, /*#__PURE__*/ React.createElement("div", {
            className: classnames('radio-button-group__container', {
                'radio-button-group__label--disabled': item.disabled,
                'radio-button-group__label--checked': item.value === selectedValue,
                'radio-button-group__label--error': hasError
            })
        }, /*#__PURE__*/ React.createElement("label", {
            className: classnames('radio-button-group__label', {
                'radio-button-group__label--disabled': item.disabled,
                'radio-button-group__label--checked': item.value === selectedValue,
                'radio-button-group__label--error': hasError
            })
        }, /*#__PURE__*/ React.createElement("input", {
            className: "radio-button-group__input",
            name: scope,
            "data-t": item.value,
            type: "radio",
            value: item.value,
            checked: item.value === selectedValue,
            "aria-checked": item.value === selectedValue,
            disabled: item.disabled,
            onChange: handleChange
        }), /*#__PURE__*/ React.createElement("div", {
            className: classnames('radio-button-group__radio', {
                'radio-button-group__radio--disabled': item.disabled,
                'radio-button-group__radio--checked': item.value === selectedValue,
                'radio-button-group__radio--error': hasError
            })
        }), /*#__PURE__*/ React.createElement("div", {
            className: "radio-button-group__text"
        }, /*#__PURE__*/ React.createElement("span", null, item.label), item.subtext && /*#__PURE__*/ React.createElement("span", {
            className: "radio-button-group__subtext"
        }, item.subtext)), item.aside && /*#__PURE__*/ React.createElement("span", {
            className: "radio-button-group__aside"
        }, item.aside)), item.content && item.value === selectedValue && /*#__PURE__*/ React.createElement("div", {
            className: classnames('radio-button-group__content', {
                'radio-button-group__label--disabled': item.disabled,
                'radio-button-group__label--checked': item.value === selectedValue,
                'radio-button-group__label--error': hasError
            })
        }, item.content)))
    )));
};

const variants = {
    enter: ({ index  })=>({
            y: 0,
            opacity: 1,
            transition: {
                delay: index * 0.05,
                duration: 0.2,
                ease: 'easeIn'
            }
        })
    ,
    exit: {
        y: 10,
        opacity: 0
    }
};
var RadioButtonGroupWithContent = (_param)=>{
    var { initialValue , onChange , value  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "initialValue",
        "onChange",
        "value"
    ]);
    const initialSelectedValue = value ? value : initialValue;
    const [selectedValue, setSelectedValue] = React.useState(initialSelectedValue || '');
    const handleChange = React.useCallback((item)=>{
        if (!item.disabled) {
            const newSelectedValue = item.value;
            setSelectedValue(newSelectedValue);
            onChange(newSelectedValue);
        }
    }, [
        onChange
    ]);
    React.useEffect(()=>{
        /**
     * On mount:
     * 1. check is there is a default `value`
     * 2. Check if the default `value` also exists in the items array
     * If condition 1 and 2 are met, call the onChange callBack fn
     */ if (!isNil$1(value) && props.items.some((item)=>item.value === value
        )) {
            onChange(value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(()=>{
        if (!isNil$1(value)) {
            setSelectedValue(value);
        }
    }, [
        value
    ]);
    const { items , scope , className , hasError  } = props;
    const classNames = classnames('radio-button-group-with-content', className);
    return /*#__PURE__*/ React.createElement("ul", {
        className: classNames,
        "data-t": props['data-t'],
        role: "radiogroup"
    }, /*#__PURE__*/ React.createElement(AnimatePresence, null, items.length > 0 && items.map((item, index)=>/*#__PURE__*/ React.createElement(motion.li, {
            className: "radio-button-group-with-content__item",
            key: item.value,
            custom: {
                index
            },
            variants: variants,
            initial: "exit",
            animate: "enter",
            role: "presentation"
        }, /*#__PURE__*/ React.createElement("div", {
            className: classnames('radio-button-group-with-content__container', {
                'radio-button-group-with-content__container--disabled': item.disabled && item.value !== selectedValue,
                'radio-button-group-with-content__container--checked': item.value === selectedValue,
                'radio-button-group-with-content__container--error': hasError
            })
        }, /*#__PURE__*/ React.createElement("label", {
            className: classnames('radio-button-group-with-content__label', {
                'radio-button-group-with-content__label--disabled': item.disabled && item.value !== selectedValue,
                'radio-button-group-with-content__label--error': hasError
            })
        }, /*#__PURE__*/ React.createElement("input", {
            className: "radio-button-group-with-content__input",
            name: scope,
            "data-t": item.value,
            type: "radio",
            value: item.value,
            checked: item.value === selectedValue,
            "aria-checked": item.value === selectedValue,
            disabled: item.disabled && item.value !== selectedValue,
            onChange: ()=>handleChange(item)
        }), /*#__PURE__*/ React.createElement("div", {
            className: classnames('radio-button-group-with-content__radio', {
                'radio-button-group-with-content__radio--disabled': item.disabled,
                'radio-button-group-with-content__radio--checked': item.value === selectedValue,
                'radio-button-group-with-content__radio--error': hasError
            })
        }), /*#__PURE__*/ React.createElement("div", {
            className: "radio-button-group-with-content__text"
        }, /*#__PURE__*/ React.createElement("span", null, item.label), item.subtext && /*#__PURE__*/ React.createElement("span", {
            className: "radio-button-group-with-content__subtext"
        }, item.subtext)), item.aside && /*#__PURE__*/ React.createElement("span", {
            className: "radio-button-group-with-content__aside"
        }, item.aside)), item.content && item.value === selectedValue && /*#__PURE__*/ React.createElement("div", {
            className: classnames('radio-button-group-with-content__content', {
                'radio-button-group-with-content__content--disabled': item.disabled && item.value !== selectedValue,
                'radio-button-group-with-content__content--error': hasError
            })
        }, item.content)))
    )));
};

class RangeSlider extends (React__default.Component) {
    render() {
        const { min , max , initialValues , currency  } = this.props;
        return /*#__PURE__*/ React__default.createElement(SimpleRangeSlider, {
            min: min,
            max: max,
            defaultValue: initialValues,
            onAfterChange: this.onChange,
            currency: currency
        });
    }
    constructor(...args){
        super(...args);
        this.onChange = (value)=>{
            const state = {
                facet: this.props.facet,
                value: `range(${value[0] * 100} to ${value[1] * 100})`
            };
            this.props.onChange(state);
        };
    }
}
RangeSlider.defaultProps = {
    min: 0,
    max: 600,
    initialValues: [
        0,
        600
    ]
};
function SimpleRangeSlider({ min , max , defaultValue =[] , value: value1 , onChange , onAfterChange , currency , renderNumber =(v)=>v
 , ariaLabel  }) {
    const [offset, setOffset] = useState(0);
    const firstHandleRef = useRef(null);
    const secondHandleRef = useRef(null);
    useLayoutEffect(checkCollision, []);
    const valueOrDefaultValue = value1 ? {
        value: value1
    } : {
        defaultValue
    };
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "range-slider"
    }, /*#__PURE__*/ React__default.createElement(Range, swcHelpers.extends({}, valueOrDefaultValue, {
        min: min,
        max: max,
        handle: renderHandle,
        onAfterChange: onAfterChange,
        onChange: handleChange,
        allowCross: false
    })));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function renderHandle(_param) {
        var { value , dragging , index , className  } = _param, restProps = swcHelpers.objectWithoutProperties(_param, [
            "value",
            "dragging",
            "index",
            "className"
        ]);
        let collider;
        let offsetStyle;
        if (className && className.includes('1')) {
            collider = firstHandleRef;
            offsetStyle = {
                left: offset * -1
            };
        } else if (className && className.includes('2')) {
            collider = secondHandleRef;
            offsetStyle = {
                right: offset * -1 - 10
            };
        }
        return /*#__PURE__*/ React__default.createElement(Handle, swcHelpers.extends({
            key: index,
            value: value,
            className: className
        }, restProps, {
            ariaLabel: ariaLabel
        }), /*#__PURE__*/ React__default.createElement("div", {
            ref: collider,
            className: "tooltip tooltip--collider",
            style: offsetStyle
        }), /*#__PURE__*/ React__default.createElement("div", {
            className: "tooltip tooltip--label",
            style: offsetStyle
        }, currency ? /*#__PURE__*/ React__default.createElement(Price, {
            value: renderNumber(value),
            currency: currency,
            centAmount: false
        }) : renderNumber(value)));
    }
    function handleChange(value) {
        onChange && onChange(value);
        checkCollision();
    }
    function checkCollision() {
        if (firstHandleRef.current && secondHandleRef.current) {
            const firstHandle = firstHandleRef.current.getBoundingClientRect();
            const secondHandle = secondHandleRef.current.getBoundingClientRect();
            if (firstHandle.right > secondHandle.left) {
                setOffset((firstHandle.right - secondHandle.left) / 2);
            } else {
                setOffset(0);
            }
        }
    }
}

const regHex = /#([a-f0-9]{3}){1,2}\b/i;
const regPresetBg = /black|grey|white/;
const alignmentOpts = [
    'left',
    'right',
    'center'
];
const variantReverseMap = Object.entries(variantMap).reduce((acc, [key, value])=>swcHelpers.objectSpread({}, acc, {
        [value]: key
    })
, {});
const isElement = (domNode)=>'attribs' in domNode
;
const isAlignment = (alignment)=>alignmentOpts.includes(alignment)
;
const getOptions = (textColor)=>{
    return {
        replace: (domNode)=>{
            if (isElement(domNode) && domNode.name in variantReverseMap) {
                var ref;
                let align = 'left';
                if ((ref = domNode.attribs.style) === null || ref === void 0 ? void 0 : ref.includes('text-align')) {
                    const styles = domNode.attribs.style.split(';');
                    const textAlign = styles.find((s)=>s.includes('text-align')
                    );
                    const [_, value] = textAlign ? textAlign.split(':') : [];
                    align = value && isAlignment(value) ? value : 'left';
                }
                return /*#__PURE__*/ React.createElement(Heading, {
                    variant: variantReverseMap[domNode.name],
                    align: align,
                    color: textColor === 'white' ? 'alt' : undefined
                }, domToReact(domNode.children));
            }
            return domNode;
        }
    };
};
const RichTextArea = ({ content , className , textColor ='black' , backgroundColor ='transparent' , fullWidth  })=>{
    const classNames = classnames('rich-text-area', `rich-text-area--text-${textColor}`, {
        'rich-text-area--full-width': fullWidth
    }, className);
    const richTextAreaStyle = {
        backgroundColor: regHex.test(backgroundColor) ? backgroundColor : regPresetBg.test(backgroundColor) ? colorHexMap[backgroundColor] : 'transparent'
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames,
        style: richTextAreaStyle
    }, parseHtml(content, getOptions(textColor)));
};

/**
 * By setting the key attribute on this component and changing the value, you can re-mount this component
 * and trigger a remount to have it scroll to the element again. Example:
 * <ScrollIntoView key={changingValue}><Child /></ScrollIntoView>
 */ const ScrollIntoView = ({ children  })=>{
    const { elementRef , triggerScroll  } = useScrollIntoView();
    React.useEffect(()=>{
        triggerScroll();
    }, [
        triggerScroll
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: "scroll-into-view",
        "data-t": "scroll-into-view",
        ref: elementRef
    }, children);
};

const ScrollUpButton = ({ showPosition =400 , stopPosition =0  })=>{
    const { getLabel  } = useIntl();
    const [show, setShow] = React.useState(false);
    React.useEffect(()=>{
        toggleScrollTopButton();
    }, []);
    const handleClick = React.useCallback(()=>{
        window.scrollTo(0, stopPosition);
    }, [
        stopPosition
    ]);
    const toggleScrollTopButton = React.useCallback(()=>{
        setShow(showPosition < window.pageYOffset);
    }, [
        showPosition
    ]);
    useEventListener('scroll', toggleScrollTopButton);
    const classNames = classnames('scroll-to-top', {
        'scroll-to-top--show': show
    });
    return /*#__PURE__*/ React.createElement("button", {
        "aria-label": getLabel('common.scrollUp'),
        className: classNames,
        onClick: handleClick
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-up"
    }));
};

const SelectionGroupItem = ({ title , text , value , scope , aside , handleChange , isChecked , disabled  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: "selection-group-item"
    }, /*#__PURE__*/ React.createElement("input", {
        className: "selection-group-item__input",
        name: scope,
        id: title,
        type: "radio",
        value: value,
        checked: isChecked,
        "aria-checked": isChecked,
        disabled: disabled || undefined,
        onChange: handleChange
    }), /*#__PURE__*/ React.createElement("label", {
        className: "selection-group-item__label",
        htmlFor: title
    }, /*#__PURE__*/ React.createElement("div", {
        className: "selection-group-item__main"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        className: "selection-group-item__title"
    }, title), text && /*#__PURE__*/ React.createElement("span", {
        className: "selection-group-item__text"
    }, text)), aside && /*#__PURE__*/ React.createElement("div", {
        className: "selection-group-item__aside"
    }, aside)));
};

class SelectionGroup extends React.Component {
    render() {
        let { selectedItem  } = this.props;
        const { items  } = this.props;
        if (!selectedItem) {
            selectedItem = items[0].value;
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: "selection-group"
        }, items.map((item)=>/*#__PURE__*/ React.createElement(SelectionGroupItem, swcHelpers.extends({}, item, {
                key: item.value,
                isChecked: item.value === selectedItem,
                handleChange: this.handleChange
            }))
        ));
    }
    constructor(...args){
        super(...args);
        this.state = {
            value: ''
        };
        this.handleChange = (event)=>{
            const value = event.currentTarget.value;
            this.setState({
                value
            });
            this.props.onChange && this.props.onChange(value);
        };
    }
}

const Sticky = ({ children , top , bottomBoundary , enabled  })=>/*#__PURE__*/ React.createElement(StickyNode, {
        top: top,
        bottomBoundary: bottomBoundary,
        enabled: enabled
    }, children)
;

const StickyCallToAction = ({ price , perBox , button , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('sticky-call-to-action', {
            'sticky-call-to-action--no-price': !price
        }, className)
    }, price && /*#__PURE__*/ React.createElement("div", {
        className: "sticky-call-to-action__price-info"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "sticky-call-to-action__price"
    }, price), perBox && /*#__PURE__*/ React.createElement("span", {
        className: "sticky-call-to-action__per-box"
    }, perBox)), button)
;

const statusMap = {
    IN_STOCK: (quantity)=>`in-stock${quantity ? '-with-quantity' : ''}`
    ,
    LOW_STOCK: (quantity)=>`low-stock${quantity ? '-with-quantity' : ''}`
    ,
    OUT_OF_STOCK: ()=>'out-of-stock'
    ,
    UNKNOWN: ()=>'unknown-stock'
};
const useGetStockStatusIdentifier = (stockStatus, quantity)=>React.useMemo(()=>{
        const status = statusMap[stockStatus];
        return status(quantity);
    }, [
        stockStatus,
        quantity
    ])
;

const useGetStockStatusLabel = (identifier, quantity1)=>{
    const { getLabel , getFormattedLabel  } = useIntl();
    return React.useMemo(()=>{
        const labelMap = {
            'in-stock': ()=>getLabel('common.stockIndicator.inStock')
            ,
            'in-stock-with-quantity': (quantity)=>{
                return getFormattedLabel('common.stockIndicator.inStockWithQuantity', {
                    labelData: {
                        quantity: quantity === null || quantity === void 0 ? void 0 : quantity.toString()
                    }
                });
            },
            'low-stock': ()=>getLabel('common.stockIndicator.lowStock')
            ,
            'low-stock-with-quantity': (quantity)=>{
                return getFormattedLabel('common.stockIndicator.lowStockWithQuantity', {
                    labelData: {
                        quantity: quantity === null || quantity === void 0 ? void 0 : quantity.toString()
                    }
                });
            },
            'out-of-stock': ()=>getLabel('common.stockIndicator.outOfStock')
            ,
            'unknown-stock': ()=>getLabel('common.stockIndicator.unknownStock')
        };
        const label = labelMap[identifier];
        return label(quantity1);
    }, [
        getFormattedLabel,
        getLabel,
        quantity1,
        identifier
    ]);
};

const StockIndicator = ({ status , quantity , additionalLabel  })=>{
    const identifier = useGetStockStatusIdentifier(status, quantity);
    const label = useGetStockStatusLabel(identifier, quantity);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('common-stock-indicator', `common-stock-indicator--${identifier}`)
    }, /*#__PURE__*/ React.createElement("span", {
        className: "common-stock-indicator__level"
    }), /*#__PURE__*/ React.createElement(Text, {
        variant: "caption-head"
    }, label, "\xa0", additionalLabel && /*#__PURE__*/ React.createElement("span", {
        className: "common-stock-indicator__additional"
    }, additionalLabel)));
};

const ShowroomModelIndicator = ({ label , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('common-showroommodel-indicator', className)
    }, /*#__PURE__*/ React.createElement("span", {
        className: "common-showroommodel-indicator__dot"
    }), /*#__PURE__*/ React.createElement(Text, {
        variant: "caption-head"
    }, label))
;

const SummaryContainer = (_param)=> {
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return React.createElement("article", swcHelpers.extends({
        className: classnames('summary__container', className)
    }, rest), children);
};

const Summary = ({ children , onEditClick , onDeleteClick , 'data-t': dataT = 'summary' , className , editRef , 'aria-label': ariaLabel = '' ,  })=>{
    const { getLabel  } = useIntl();
    const withButtons = !!(onEditClick || onDeleteClick);
    return /*#__PURE__*/ React.createElement(SummaryContainer, {
        className: classnames('summary', className),
        "data-t": dataT
    }, withButtons && /*#__PURE__*/ React.createElement("div", {
        className: "summary__buttons"
    }, onEditClick && /*#__PURE__*/ React.createElement(IconButton, {
        className: "summary__button",
        "data-t": `${dataT}-edit-button`,
        onClick: onEditClick,
        ref: editRef,
        "aria-label": `${getLabel('common.actions.edit')} ${ariaLabel}`
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "pencil",
        className: "summary__button-icon"
    })), onDeleteClick && /*#__PURE__*/ React.createElement(IconButton, {
        className: "summary__button",
        "data-t": `${dataT}-delete-button`,
        onClick: onDeleteClick,
        "aria-label": `${getLabel('common.actions.delete')} ${ariaLabel}`
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "trash",
        className: "summary__button-icon"
    }))), children);
};

const joinData = (values)=>values.filter((v)=>!!v
    ).join(' ')
;
const SummaryItem = (value)=>{
    if (!value || Array.isArray(value) && value.every((val)=>!val
    )) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("span", null, joinData(Array.isArray(value) ? value : [
        value
    ]));
};

const SummaryLoader = ()=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React__default.createElement(SummaryContainer, null, /*#__PURE__*/ React__default.createElement(SkeletonLoader, null, /*#__PURE__*/ React__default.createElement("svg", {
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        height: "60px",
        "aria-label": getLabel('common.loadingText')
    }, /*#__PURE__*/ React__default.createElement("rect", {
        width: "200",
        height: "16px",
        rx: "4",
        fill: "currentColor"
    }), /*#__PURE__*/ React__default.createElement("rect", {
        width: "180",
        height: "16px",
        rx: "4",
        fill: "currentColor",
        y: "32"
    }))));
};

const AdditionalInfo = ({ title , description , firstLink , secondLink  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('additional-info', {
            'additional-info__flex': !description
        })
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: "support-footer__title"
    }, title), /*#__PURE__*/ React.createElement("div", {
        className: "support-footer__contact-details"
    }, description && /*#__PURE__*/ React.createElement(Text, {
        className: "support-footer__description"
    }, description), /*#__PURE__*/ React.createElement("div", {
        className: "info",
        "data-t": "additionalInfo"
    }, firstLink.label && /*#__PURE__*/ React.createElement(Link, {
        iconBefore: firstLink.icon,
        url: firstLink.url,
        className: "support-footer__link"
    }, firstLink.label), secondLink.label && /*#__PURE__*/ React.createElement(Link, {
        iconBefore: secondLink.icon,
        url: secondLink.url,
        className: "support-footer__link"
    }, secondLink.label))));
};

const SupportFooter = ({ message , phoneNumber , email , cartConfiguration  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: "support-footer"
    }, cartConfiguration && /*#__PURE__*/ React.createElement(AdditionalInfo, swcHelpers.extends({}, cartConfiguration)), /*#__PURE__*/ React.createElement("div", {
        className: "support-footer__container"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: "support-footer__title"
    }, parseHtml(message)), /*#__PURE__*/ React.createElement("div", {
        className: "support-footer__contact-details"
    }, phoneNumber && /*#__PURE__*/ React.createElement(Link, {
        iconBefore: "phone",
        url: `tel:${phoneNumber}`,
        className: "support-footer__link"
    }, phoneNumber), email && /*#__PURE__*/ React.createElement(Link, {
        iconBefore: "mail",
        url: `mailto:${email}`,
        className: "support-footer__link"
    }, email))));
};

const Survey = ({ title , surveyUrl  })=>/*#__PURE__*/ React__default.createElement("div", {
        className: "survey-content"
    }, /*#__PURE__*/ React__default.createElement("iframe", {
        className: "survey-iframe",
        src: surveyUrl,
        title: title
    }))
;

const Tab = /*#__PURE__*/ React.forwardRef((_param, ref)=> {
    var { label , onClick , isActive , className , variant ='primary'  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "label",
        "onClick",
        "isActive",
        "className",
        "variant"
    ]);
    return React.createElement("button", swcHelpers.extends({}, rest, {
        onClick: onClick,
        className: classnames(`tab tab--${variant}`, className, {
            _is_active: isActive
        }),
        "aria-pressed": isActive
    }), label);
});
Tab.displayName = 'Tab';

const Tooltip = (_param)=>{
    var { message , id , className , placement: placement1 = 'top' , parseHtml: parseHtml$1 =false , dashed , label , greyedOut  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "message",
        "id",
        "className",
        "placement",
        "parseHtml",
        "dashed",
        "label",
        "greyedOut"
    ]);
    const { getLabel  } = useIntl();
    const currentBreakpoint = useBreakpoint();
    const [isActive1, setIsActive] = React.useState(false);
    const [tooltipHovered, setTooltipHovered] = React.useState(false);
    const isTouch = React.useMemo(()=>isTouchDevice()
    , []);
    const triggerRef = React.useRef(null);
    // Touch device handlers
    const handleDocumentClick = React.useCallback((e)=>{
        // Check if the clicked target is not equal to our trigger, preventing the tooltip to re-open immediately
        setIsActive((isActive)=>{
            if (isActive && triggerRef.current && !triggerRef.current.contains(e.target)) {
                return false;
            }
            return isActive;
        });
    }, []);
    React.useEffect(()=>{
        if (!isTouch) {
            return;
        }
        document.addEventListener('touchend', handleDocumentClick);
        return ()=>document.removeEventListener('touchend', handleDocumentClick)
        ;
    }, [
        isTouch,
        handleDocumentClick
    ]);
    const handleTriggerClick = React.useCallback(()=>{
        isTouch && setIsActive((isActive)=>!isActive
        );
    }, [
        isTouch
    ]);
    // Non-touch handlers
    const handleTriggerMouseOver = React.useCallback(()=>{
        !isTouch && setIsActive(true);
    }, [
        isTouch
    ]);
    const handleTriggerMouseLeave = React.useCallback(()=>{
        // this timeout allows a user to move it's cursor from the trigger to the tooltip to keep the tooltip open
        !isTouch && setTimeout(()=>setIsActive(false)
        , 100);
    }, [
        isTouch
    ]);
    const handleTooltipMouseOver = React.useCallback(()=>{
        !isTouch && setTooltipHovered(true);
    }, [
        isTouch
    ]);
    const handleTooltipMouseLeave = React.useCallback(()=>{
        !isTouch && setTooltipHovered(false);
    }, [
        isTouch
    ]);
    const handleKeyPress = (e)=>{
        if (e.key === 'Enter') {
            !isTouch && setIsActive(true);
            if (!isTouch && isActive1) {
                setIsActive(false);
            }
        }
        if (e.key === 'Escape') {
            setIsActive(false);
        }
    };
    const renderDashed = dashed && (currentBreakpoint === BreakpointName.DESKTOP || currentBreakpoint === BreakpointName.TABLET_LANDSCAPE);
    const buttonClass = classnames({
        tooltip__button: !renderDashed,
        'tooltip__button--active': isActive1 && !renderDashed,
        'tooltip__button--greyed-out': !dashed && greyedOut,
        tooltip__dashed: renderDashed
    });
    if (!message) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('tooltip', className)
    }, /*#__PURE__*/ React.createElement(Manager, null, /*#__PURE__*/ React.createElement(Reference, null, ({ ref  })=>/*#__PURE__*/ React.createElement("div", {
            ref: triggerRef
        }, /*#__PURE__*/ React.createElement("div", {
            className: "tooltip__container"
        }, /*#__PURE__*/ React.createElement("button", {
            type: "button",
            "aria-label": rest['aria-label'] || getLabel('common.tooltip'),
            "aria-describedby": id,
            "aria-expanded": isActive1,
            className: buttonClass,
            ref: ref,
            onClick: handleTriggerClick,
            onMouseOver: handleTriggerMouseOver,
            onMouseLeave: handleTriggerMouseLeave,
            onFocus: ()=>null
            ,
            onKeyUp: handleKeyPress
        }, renderDashed && label, !renderDashed && /*#__PURE__*/ React.createElement(Icon, {
            name: "questionmark"
        }))))
    ), /*#__PURE__*/ React.createElement("div", {
        "aria-live": "polite",
        id: id
    }, (isActive1 || tooltipHovered) && /*#__PURE__*/ React.createElement(Popper, {
        placement: placement1,
        modifiers: {
            preventOverflow: {
                enabled: true,
                boundariesElement: 'scrollParent',
                padding: 0
            }
        }
    }, ({ ref , style , placement  })=>/*#__PURE__*/ React.createElement("div", {
            "aria-hidden": !isActive1,
            ref: ref,
            style: style,
            "data-placement": placement,
            className: "tooltip__message",
            onMouseOver: handleTooltipMouseOver,
            onMouseLeave: handleTooltipMouseLeave,
            onFocus: ()=>null
            ,
            onBlur: ()=>setIsActive(false)
        }, typeof message === 'string' ? /*#__PURE__*/ React.createElement("div", null, parseHtml$1 ? parseHtml(message) : message) : /*#__PURE__*/ React.isValidElement(message) && message)
    ))));
};

const TOAST_INFO = 'TOAST/info';
const TOAST_SUCCESS = 'TOAST/success';
const TOAST_FAILED = 'TOAST/failed';
const TOAST_WARNING = 'TOAST/warning';
const buildContent = (name, label)=>{
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Icon, {
        className: "Toastify__toast-icon",
        name: name
    }), /*#__PURE__*/ React.createElement("span", {
        className: "Toastify__toast-label"
    }, label));
};
const toastService = ()=>{
    let getLabel;
    const showToast = (type)=>(labelKey)=>{
            const label = getLabel ? getLabel(labelKey) : '';
            switch(type){
                case TOAST_INFO:
                    toast.info(buildContent('information-circle', label));
                    break;
                case TOAST_SUCCESS:
                    toast.success(buildContent('checkmark-circle', label));
                    break;
                case TOAST_FAILED:
                    toast.error(buildContent('alert-circle', label));
                    break;
                case TOAST_WARNING:
                    toast.warn(buildContent('warning', label));
                    break;
            }
        }
    ;
    const init = (getter)=>{
        getLabel = getter;
    };
    return {
        init,
        info: showToast(TOAST_INFO),
        success: showToast(TOAST_SUCCESS),
        failed: showToast(TOAST_FAILED),
        warning: showToast(TOAST_WARNING)
    };
};
var toastService$1 = toastService();

const defaultPositionOption = toast.POSITION.BOTTOM_CENTER;
const ToastContainer = ()=>{
    const { getLabel  } = useIntl();
    React.useEffect(()=>toastService$1.init(getLabel)
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(ToastContainer$2, {
        autoClose: 6000,
        closeButton: /*#__PURE__*/ React.createElement(Icon, {
            name: "close"
        }),
        position: defaultPositionOption,
        transition: Slide,
        newestOnTop: true
    });
};
var ToastContainer$1 = /*#__PURE__*/ React.memo(ToastContainer);

const USPList = ({ usps , className  })=>{
    const { getLabel  } = useIntl();
    const items = React.useMemo(()=>usps.map((usp)=>getLabel(usp, {
                hideLabelKey: true
            })
        ).filter((item)=>Boolean(item)
        )
    , [
        getLabel,
        usps
    ]);
    return /*#__PURE__*/ React.createElement(List, {
        icon: "checkmark",
        items: items,
        className: className
    });
};

const getRest = (props)=>{
    const rest = swcHelpers.objectWithoutProperties(props, [
        "className",
        "index",
        "number",
        "label",
        "status",
        "hideLabel",
        "onClick",
        "disabled"
    ]);
    return rest;
};
const getSharedProps = (props)=>{
    const { className , disabled , status ='open'  } = props;
    return swcHelpers.objectSpread({
        className: classnames('step', `step--${status}`, className),
        'aria-disabled': disabled,
        'aria-current': status === 'active' ? 'step' : undefined
    }, disabled ? {
        disabled
    } : {});
};
const Step = (props)=>{
    const { onClick , disabled , label , index , hideLabel , number , status ='open'  } = props;
    const onClickHandler = React__default.useCallback(()=>{
        if (onClick && !disabled) {
            onClick(label, index);
        }
    }, [
        label,
        index,
        disabled,
        onClick
    ]);
    // Only render this component as a button when there is a click handler
    if (!!onClick) {
        return /*#__PURE__*/ React__default.createElement("button", swcHelpers.extends({}, getRest(props), getSharedProps(props), {
            onClick: onClickHandler
        }), /*#__PURE__*/ React__default.createElement(StepContent, {
            number: number,
            hideLabel: hideLabel,
            label: label,
            status: status
        }));
    } else {
        return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({}, getRest(props), getSharedProps(props), {
            role: "group"
        }), /*#__PURE__*/ React__default.createElement(StepContent, {
            number: number,
            hideLabel: hideLabel,
            label: label,
            status: status
        }));
    }
};
const StepContent = ({ number , hideLabel , label , status  })=>{
    const { getLabel  } = useIntl();
    const accessibleStepLabel = getLabel('common.step');
    const accessibleStatusLabel = status === 'active' && getLabel('common.active') || status === 'skip' && getLabel('common.skipped') || status === 'done' && getLabel('common.done');
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("span", {
        className: "step__number",
        "aria-hidden": true
    }, number), /*#__PURE__*/ React__default.createElement(Icon, {
        className: "step__checkmark",
        name: "checkmark"
    }), accessibleStepLabel && /*#__PURE__*/ React__default.createElement("span", {
        className: "step__visually-hidden"
    }, `${accessibleStepLabel} ${number}`), /*#__PURE__*/ React__default.createElement("span", {
        className: classnames('step__label', {
            'step__label--hide-label': hideLabel
        })
    }, label), accessibleStatusLabel && /*#__PURE__*/ React__default.createElement("span", {
        className: "step__visually-hidden"
    }, ". ", accessibleStatusLabel));
};

const getStatus = (active, index, skip)=>{
    if (active === index) {
        return 'active';
    } else if (skip) {
        return 'skip';
    } else if (active > index) {
        return 'done';
    } else {
        return 'open';
    }
};
const WizardProgress = (_param)=>{
    var { className , steps , active , withSeparators =false , labelStyle , onClick , skipSteps , disableSteps  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "steps",
        "active",
        "withSeparators",
        "labelStyle",
        "onClick",
        "skipSteps",
        "disableSteps"
    ]);
    return /*#__PURE__*/ React__default.createElement("ol", swcHelpers.extends({}, rest, {
        className: classnames('wizard-progress', className, {
            'wizard-progress--with-inline-labels': labelStyle === 'inline',
            'wizard-progress--with-separators': withSeparators && labelStyle !== 'inline'
        })
    }), steps.map((step, index)=>{
        const isSkip = skipSteps === null || skipSteps === void 0 ? void 0 : skipSteps.includes(index);
        const isDisabled = disableSteps === null || disableSteps === void 0 ? void 0 : disableSteps.includes(index);
        return /*#__PURE__*/ React__default.createElement("li", {
            className: "wizard-progress__item",
            key: step
        }, /*#__PURE__*/ React__default.createElement(Step, {
            index: index,
            number: index + 1,
            label: step,
            className: classnames({
                'wizard-progress__step--with-spacing': !withSeparators && labelStyle === 'hidden'
            }),
            disabled: isSkip || isDisabled || false,
            hideLabel: labelStyle === 'hidden',
            status: getStatus(active, index, isSkip || false),
            onClick: onClick,
            "data-t": `wizardStep${index + 1}`
        }));
    }));
};

const SelectedChoices = (_param)=> {
    var { className , choices  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "choices"
    ]);
    return React.createElement("ul", swcHelpers.extends({}, rest, {
        className: classnames('selected-choices', className)
    }), Object.entries(choices).map(([choice, { name , action , onClick  }], index)=>/*#__PURE__*/ React.createElement("li", {
            key: `${choice}-${name}`,
            className: "selected-choices__list-item"
        }, /*#__PURE__*/ React.createElement("button", {
            className: "selected-choices__button",
            onClick: onClick,
            type: "button"
        }, /*#__PURE__*/ React.createElement(Icon, {
            className: "selected-choices__checkmark",
            name: "checkmark",
            width: 30,
            height: 30
        }), /*#__PURE__*/ React.createElement(Text, {
            className: "selected-choices__name",
            variant: "caption"
        }, name), /*#__PURE__*/ React.createElement(Text, {
            variant: "caption-head",
            "data-t": `changeSelectedChoice${index + 1}`,
            color: "primary"
        }, action)))
    ));
};

const Breadcrumbs = ({ pages  })=>{
    if (!pages) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "breadcrumbs"
    }, pages.map((page, index)=>index + 1 < pages.length ? /*#__PURE__*/ React.createElement("div", {
            key: page.name,
            className: "breadcrumbs__link-wrapper"
        }, /*#__PURE__*/ React.createElement("a", {
            href: page.url,
            className: "breadcrumbs__link"
        }, page.name)) : /*#__PURE__*/ React.createElement("span", {
            key: page.name,
            className: "breadcrumbs__current-page-text"
        }, page.name)
    ), /*#__PURE__*/ React.createElement(BreadcrumbList, {
        breadcrumbs: pages.slice(0, -1)
    }));
};

const InfinitePagination = ({ onClick , buttonLabel , total , loaded , isLoading , labelKey , className , pageNr: pagination ,  })=>{
    const { getFormattedLabel  } = useIntl();
    const label = labelKey && getFormattedLabel(labelKey, {
        labelData: {
            total,
            loaded: loaded > total ? total : loaded
        }
    });
    const href = `?page=${pagination ? pagination + 1 : 2}`;
    const clickHandler = (e)=>{
        e.preventDefault();
        onClick();
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('infinite-pagination', className)
    }, /*#__PURE__*/ React.createElement(ProgressBar, {
        value: loaded,
        max: total,
        className: "infinite-pagination__progress"
    }), label && /*#__PURE__*/ React.createElement("div", {
        className: "infinite-pagination__label"
    }, label), loaded < total && /*#__PURE__*/ React.createElement(Button, {
        className: "infinite-pagination__button",
        "data-t": "pagination-load-more",
        caption: buttonLabel,
        href: href,
        onClick: clickHandler,
        variant: "outline",
        isLoading: isLoading
    }));
};
var InfinitePagination$1 = /*#__PURE__*/ React.memo(InfinitePagination);

// see https://polyfill.io/v3/url-builder/ for details
const FEATURES = [
    'default',
    'es2015',
    'es2016',
    'es2017',
    'es2018',
    'es5',
    'es6',
    'es7',
    'fetch',
    'IntersectionObserver',
    'smoothscroll',
    'globalThis', 
];
const Polyfill = ({ features =[]  })=>{
    const filename = process.env.NODE_ENV === 'production' ? 'polyfill.min.js' : 'polyfill.js';
    const params = QueryString.stringify({
        rum: false,
        unknown: 'polyfill',
        flags: 'gated',
        features: uniq([
            ...FEATURES,
            ...features
        ]).join(',')
    }, true);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("link", {
        rel: "preconnect",
        href: "https://polyfill.io"
    }), /*#__PURE__*/ React.createElement("script", {
        src: `https://polyfill.io/v3/${filename}?${params}`
    }));
};
function uniq(arr) {
    return [
        ...new Set(arr)
    ];
}

const SliderCarousel = ({ children , itemWidth =280  })=>/*#__PURE__*/ React__default.createElement("ul", {
        className: "slider-carousel"
    }, React__default.Children.map(children, (child, i)=>/*#__PURE__*/ React__default.createElement("li", {
            key: i,
            className: "slider-carousel__item",
            style: {
                minWidth: `calc(${itemWidth}px + 1rem)`
            }
        }, child)
    ))
;

const ProductOffer = (_param)=>{
    var { title , description , imageUrl , fallbackImageUrl , linkTarget , url , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "title",
        "description",
        "imageUrl",
        "fallbackImageUrl",
        "linkTarget",
        "url",
        "className"
    ]);
    const sharedProps = {
        className: classnames('product-offer__image', {
            'product-offer__image--fallback': !imageUrl
        }),
        'data-t': 'ImageContainer'
    };
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('product-offer', className)
    }), url ? /*#__PURE__*/ React.createElement("a", {
        href: url,
        "data-t": "ImageAnchor",
        target: linkTarget,
        rel: linkTarget === '_blank' ? 'noreferrer noopener' : undefined
    }, /*#__PURE__*/ React.createElement("span", swcHelpers.extends({}, sharedProps), /*#__PURE__*/ React.createElement("img", {
        src: imageUrl !== null && imageUrl !== void 0 ? imageUrl : fallbackImageUrl,
        "data-t": "Image",
        alt: title
    }))) : /*#__PURE__*/ React.createElement("div", swcHelpers.extends({}, sharedProps), /*#__PURE__*/ React.createElement("img", {
        src: imageUrl !== null && imageUrl !== void 0 ? imageUrl : fallbackImageUrl,
        "data-t": "Image",
        alt: title
    })), /*#__PURE__*/ React.createElement(Heading, {
        component: "h4",
        variant: "subheading1",
        className: "product-offer__title"
    }, url ? /*#__PURE__*/ React.createElement("a", {
        href: url,
        "data-t": "TitleAnchor",
        target: linkTarget,
        rel: linkTarget === '_blank' ? 'noreferrer noopener' : undefined
    }, title) : title), description && /*#__PURE__*/ React.createElement("p", null, description));
};

const ProductOffers = (_param)=>{
    var { className , labels , offers , fallbackImage , linkTarget  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "labels",
        "offers",
        "fallbackImage",
        "linkTarget"
    ]);
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({}, rest, {
        className: classnames('product-offers', className)
    }), /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h3"
    }, labels.title), /*#__PURE__*/ React.createElement("div", {
        className: "product-offers__items"
    }, offers.map(({ title , description , url , imageUrl  })=>/*#__PURE__*/ React.createElement(ProductOffer, {
            key: title,
            className: "product-offers__offer",
            title: title,
            description: description,
            url: url,
            imageUrl: imageUrl,
            fallbackImageUrl: fallbackImage,
            linkTarget: linkTarget
        })
    )));
};

const ProductOffersContainer = (_param)=>{
    var { labels: labelsProp  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "labels"
    ]);
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>labelsProp !== null && labelsProp !== void 0 ? labelsProp : {
            title: getLabel('products.offers.title')
        }
    , [
        getLabel,
        labelsProp
    ]);
    return /*#__PURE__*/ React.createElement(ProductOffers, swcHelpers.extends({}, props, {
        labels: labels
    }));
};

const ReorderButton = (_param)=>{
    var { url , tabIndex  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "url",
        "tabIndex"
    ]);
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("a", swcHelpers.extends({
        href: url,
        className: "reorder-button header-menu__icon",
        tabIndex: tabIndex
    }, rest), getLabel('common.menu.reorder.button'));
};

var QuickFilterButton = (_param)=>{
    var { className , active , label , ariaLabels  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "active",
        "label",
        "ariaLabels"
    ]);
     return React.createElement(Button$1, swcHelpers.extends({}, rest, {
        className: classnames('quick-filter-button', {
            'quick-filter-button--active': active
        }, className),
        variant: "outline",
        "aria-label": active ? ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.active : ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.inactive
    }), /*#__PURE__*/ React.createElement("span", {
        className: "quick-filter-button__text"
    }, label), active && /*#__PURE__*/ React.createElement(Icon, {
        className: "quick-filter-button__icon",
        name: "close"
    }));
};

var _id, _id1;
const LinkLists = (_param)=> {
    var { className , items  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "items"
    ]);
    return React.createElement("div", swcHelpers.extends({
        className: classnames('link-lists', className)
    }, rest), items.map((column, index1)=>/*#__PURE__*/ React.createElement("div", {
            className: "link-lists__column",
            key: (_id = column.id) !== null && _id !== void 0 ? _id : column.title || `item-${index1}`
        }, column.title && /*#__PURE__*/ React.createElement(Text, {
            className: "link-lists__text",
            component: "h2"
        }, column.title), column.links.map((link, index)=>/*#__PURE__*/ React.createElement(Link, swcHelpers.extends({
                key: (_id1 = link.id) !== null && _id1 !== void 0 ? _id1 : link.label || `column-item-${index}`,
                className: "link-lists__link"
            }, link))
        ))
    ));
};

const SpotlightedStores = (_param)=>{
    var { className , items , storesUrl  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "items",
        "storesUrl"
    ]);
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({
        className: classnames('spotlighted-stores', className)
    }, rest), /*#__PURE__*/ React.createElement("div", {
        className: "site-container"
    }, /*#__PURE__*/ React.createElement(LinkLists, {
        items: items
    }), /*#__PURE__*/ React.createElement(Link, {
        className: "spotlighted-stores__link",
        label: getLabel('common.component.spotlightedStores.link'),
        url: storesUrl,
        openInNewWindow: false
    })));
};

const LinkGroup = ({ className , links  })=>{
    if (!(links === null || links === void 0 ? void 0 : links.length)) return null;
    return /*#__PURE__*/ React.createElement("ul", {
        className: classnames('link-group', className)
    }, links.map((_param)=> {
        var { icon , label , url  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
            "icon",
            "label",
            "url"
        ]);
        return React.createElement("li", {
            key: label,
            className: "link-group__item"
        }, /*#__PURE__*/ React.createElement(Link, swcHelpers.extends({
            url: url,
            className: "link-group__link"
        }, rest), icon && /*#__PURE__*/ React.createElement(Icon, {
            className: "link-group__icon",
            name: icon,
            width: 32,
            height: 32
        }), /*#__PURE__*/ React.createElement(Text, {
            className: "link-group__text",
            component: "span"
        }, label), /*#__PURE__*/ React.createElement(Icon, {
            className: "link-group__icon link-group__icon--arrow",
            name: "chevron-right"
        })));
    }));
};

export { Accordion, AccordionItem, Alert, Autocomplete, Backdrop, Breadcrumbs, Button, Card, Carousel, CartButton$1 as CartButton, CartCounter, CheckboxList, ContentTabs, Table as DataTable, DateTime, Delayed, DiscountBar, ErrorBoundary, Expandable, Expander, EyeSideIndicator, FeatureGroup, FilesList, Flyout, GoogleMaps, GoogleMapsContext, GoogleMapsProvider, IFrame, IconButton, IconLink, Image, InfinitePagination$1 as InfinitePagination, InfoWindow, LazyImage, LineItem, LineItemAside, LineItemBundleRow, LineItemContent, LineItemDeleteButton, LineItemGlassesPrescription, LineItemMedia, LineItemPrescription, LineItemRow, LineItemSubtitle, LineItemTitle, Link, LinkGroup, LinkLists, Link$1 as LinkTypes, List, Loader, LoaderScreen, LockBodyScroll, Logo, MarkerClusters, index$1 as MediaQuery, Modal, ModalFooter, NotificationBar, Olapic, OlapicContext, OlapicProvider, OptionCardGroup, Pin, Polyfill, PreloadFonts, Price, PriceTag, ProductOffersContainer as ProductOffers, ProductTag, ProgressBar, QuantitySelector, QuickFilterButton, RadioButton, RadioButtonGroup, RadioButtonGroupWithContent, RangeSlider, ReorderButton, RichTextArea, ScrollIntoView, ScrollUpButton, SelectedChoices, SelectionGroup, sharedProps as SharedProps, ShowroomModelIndicator, SimpleRangeSlider, SliderCarousel, SpotlightedStores, Sticky, StickyCallToAction, StockIndicator, Summary, SummaryContainer, SummaryItem, SummaryLoader, SupportFooter, Survey, Tab, TabLink, Tabs, ToastContainer$1 as ToastContainer, Tooltip, types as Types, USPList, index as Util, WizardProgress, toastService$1 as toast, useGoogleMapsContext, useGoogleMapsStaticMap, useOlapicContext, withGoogleMaps };
//# sourceMappingURL=index.js.map
